import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Collapse,
  Divider,
  TextField,
  FormControlLabel,
  Tooltip,
  Checkbox,
} from '@mui/material';
import { WarehouseSelect } from '../components/ui/WarehouseSelect';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Timer as TimerIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugReportIcon,
  Undo as UndoIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { canAccessWarehouseSurface, getCapabilitiesFromUser, hasCapability } from '../auth/access';
import { api, ensureUiScope } from '../services/api';
import { uiHealthStatus } from '../services/uiHealth';
import { deriveOpsState } from '../services/opsState';
import { buildContextBundle } from '../services/contextBundle';
import { OpsStateBanner } from '../components/OpsStateBanner';
import { TriageWizard } from '../components/TriageWizard';
import { GuardedActionDialog } from '../components/GuardedActionDialog';
import { PlaybookCallout, buildDocsUrl } from '../components/PlaybookCallout';
import { getConfigDrift, type ConfigDriftResponse } from '../services/configDrift';
import { runbookDocUrl } from '../data/runbookTop20';
import { DriftTable } from '../components/DriftTable';
import { ConfigDriftItSummary } from '../components/ConfigDriftItSummary';
import runtimeBlueprintsApi from '../services/runtimeBlueprintsApi';
import { scheduleDeferredWork } from '../utils/deferredWork';
import { buildArtifactFlowPath, type ReportDialogKind } from '../utils/reportFlow';
import { readAcceptanceProofCache } from '../utils/acceptanceArtifacts';
import { resolveSafetyReportOpenPath } from '../utils/safetyReportOpenNavigation';
import DiagnosticsEdgeOperationsPanel from '../components/diagnostics/DiagnosticsEdgeOperationsPanel';
import DiagnosticsOnboardDialog from '../components/diagnostics/DiagnosticsOnboardDialog';
import DiagnosticsReportSection from '../components/diagnostics/DiagnosticsReportSection';
import DiagnosticsSafeRecoveryPath from '../components/diagnostics/DiagnosticsSafeRecoveryPath';
import {
  CameraDiagnostic,
  DeviceOption,
  DiagnosticReport,
  EdgeLogsResponse,
  SafeRecoveryModel,
  UiErrorKind,
} from '../components/diagnostics/diagnosticsTypes';
import {
  classifyAxiosError,
  describeNetworkQualityFromStage,
  formatGate1NetworkQualityHint,
  isIpv4Literal,
  networkQualityItPlaybook,
  PAGE_TIMEOUT_MS,
  readDeviceOptionsCache,
  recommendedActionButtonLabel,
  sleep,
  stageTriagePriority,
  withTimeout,
  writeDeviceOptionsCache,
} from '../components/diagnostics/diagnosticsUtils';
import {
  incidentCanonicalEvidenceReadinessLabel,
  incidentCanonicalSourceContextLabel,
} from '../components/incidents/reviewCanonical';

const readDiagnosticsDeviceFromSearch = (search: string): string | null => {
  try {
    const q = new URLSearchParams(search || '');
    const v = String(q.get('device') || q.get('device_id') || '').trim();
    return v ? v : null;
  } catch {
    return null;
  }
};

const readDiagnosticsTriageFromSearch = (search: string): boolean => {
  try {
    const q = new URLSearchParams(search || '');
    return String(q.get('triage') || '').trim() === '1';
  } catch {
    return false;
  }
};

const readDiagnosticsIncidentFromSearch = (search: string): string | null => {
  try {
    const q = new URLSearchParams(search || '');
    const v = String(q.get('incident_id') || '').trim();
    return v || null;
  } catch {
    return null;
  }
};

export const diagnosticsIncidentLabel = (incident: any): string => {
  const canonical = String(
    incident?.canonical_labels?.incident_label
    || incident?.safety_case?.labels?.incident_label
    || incident?.policy_name
    || incident?.title
    || incident?.incident_type
    || ''
  ).trim();
  return canonical || 'Incident';
};

export const diagnosticsIncidentEvidenceState = (incident: any): string => {
  const canonical = incidentCanonicalEvidenceReadinessLabel(incident);
  if (canonical) return canonical;
  return String(incident?.evidence_readiness_state || '').trim().toUpperCase();
};

export const diagnosticsIncidentSourceContext = (incident: any): string => {
  return incidentCanonicalSourceContextLabel(incident);
};

export const diagnosticsIncidentProofScopeId = (incident: any): string => {
  return String(
    incident?.proof_scope?.proof_scope_id
    || incident?.safety_case?.traceability?.proof_scope_id
    || incident?.safety_case?.proof_scope?.proof_scope_id
    || ''
  ).trim();
};

const readLastDiagnosticsDeviceId = (): string | null => {
  try {
    return String(localStorage.getItem('ui.last_device_id') || '').trim() || null;
  } catch {
    return null;
  }
};

const Diagnostics: React.FC = () => {
  const { effectiveUser: user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, siteId } = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      const qSite = (sp.get('site') || sp.get('site_id') || '').trim();
      const qOrg = (sp.get('org') || sp.get('org_id') || '').trim();
      const scope = ensureUiScope();
      const site = qSite || String(scope?.siteId || '').trim();
      const org = qOrg || String(scope?.orgId || '').trim();
      return { siteId: site, orgId: org };
    } catch {
      return { siteId: '', orgId: '' };
    }
  }, [location.search]);
  const triageRequestedFromSearch = useMemo(() => readDiagnosticsTriageFromSearch(location.search || ''), [location.search]);
  const incidentIdFromSearch = useMemo(() => readDiagnosticsIncidentFromSearch(location.search || ''), [location.search]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedCameras, setExpandedCameras] = useState<Set<string>>(new Set());
  const [hours, setHours] = useState(24);
  const [deviceId, setDeviceId] = useState<string | null>(() => (
    readDiagnosticsDeviceFromSearch(window.location.search || '') || readLastDiagnosticsDeviceId()
  ));
  const deviceIdRef = useRef<string | null>(null);
  const diagnosticsReqRef = useRef<number>(0);
  const [deviceOptionsLoading, setDeviceOptionsLoading] = useState(false);
  const [deviceOptionsError, setDeviceOptionsError] = useState<string | null>(null);
  const [deviceOptions, setDeviceOptions] = useState<DeviceOption[]>([]);
  const [cameraDeviceCandidates, setCameraDeviceCandidates] = useState<string[]>([]);

  // Staged edge health (pilot diagnosis surface)
  const [edgeStagesLoading, setEdgeStagesLoading] = useState(false);
  const [edgeStagesError, setEdgeStagesError] = useState<string | null>(null);
  const [edgeStagesErrorKind, setEdgeStagesErrorKind] = useState<UiErrorKind | null>(null);
  const [edgeStages, setEdgeStages] = useState<any | null>(null);
  // Edge /health (reason codes: cloud reachability/auth drift, etc.)
  const [edgeHealthLoading, setEdgeHealthLoading] = useState(false);
  const [edgeHealthError, setEdgeHealthError] = useState<string | null>(null);
  const [edgeHealth, setEdgeHealth] = useState<any | null>(null);
  // Edge agent heartbeat (cloud-side Redis TTL) — answers “is the unit alive?” even when pipelines are warming up.
  const [deviceHeartbeatLoading, setDeviceHeartbeatLoading] = useState(false);
  const [deviceHeartbeatError, setDeviceHeartbeatError] = useState<string | null>(null);
  const [deviceHeartbeat, setDeviceHeartbeat] = useState<any | null>(null);
  // Edge FPS (cloud-side Prometheus) — answers “is the pipeline producing frames?”
  const [edgeFpsLoading, setEdgeFpsLoading] = useState(false);
  const [edgeFpsError, setEdgeFpsError] = useState<string | null>(null);
  const [edgeFps, setEdgeFps] = useState<{ in: number | null; out: number | null } | null>(null);
  // Last incident ingested by cloud (DB) — answers “is the cloud seeing new incidents from this unit?”
  const [lastIncidentLoading, setLastIncidentLoading] = useState(false);
  const [lastIncidentError, setLastIncidentError] = useState<string | null>(null);
  const [lastIncident, setLastIncident] = useState<any | null>(null);
  const [episodeRolloutLoading, setEpisodeRolloutLoading] = useState(false);
  const [episodeRolloutError, setEpisodeRolloutError] = useState<string | null>(null);
  const [episodeRollout, setEpisodeRollout] = useState<any | null>(null);
  const [episodeConvergeLoading, setEpisodeConvergeLoading] = useState(false);
  const [episodeConvergeError, setEpisodeConvergeError] = useState<string | null>(null);
  const [episodeConvergeResult, setEpisodeConvergeResult] = useState<any | null>(null);
  // DRB desired vs applied (cloud-side)
  const [drbStatusLoading, setDrbStatusLoading] = useState(false);
  const [drbStatusError, setDrbStatusError] = useState<string | null>(null);
  const [drbStatus, setDrbStatus] = useState<any | null>(null);
  // Phase 3: convergence rollup (DRB/zones/policies/FCP) - cloud-side, DB-derived
  const [, setConvergenceLoading] = useState(false);
  const [, setConvergenceError] = useState<string | null>(null);
  const [, setConvergence] = useState<any | null>(null);
  // Drift Dashboard: config_drift (edge-merged when reachable)
  const [configDriftLoading, setConfigDriftLoading] = useState(false);
  const [configDriftError, setConfigDriftError] = useState<string | null>(null);
  const [configDrift, setConfigDrift] = useState<ConfigDriftResponse | null>(null);
  const [convergeLoading, setConvergeLoading] = useState(false);
  const [convergeError, setConvergeError] = useState<string | null>(null);
  const [deviceDiagnosis, setDeviceDiagnosis] = useState<any | null>(null);
  const [deviceDiagnosisError, setDeviceDiagnosisError] = useState<string | null>(null);
  const [bringupLatest, setBringupLatest] = useState<any | null>(null);
  // Gate 1 persisted stages + alarms (cloud monitor)
  const [gate1StagesLoading, setGate1StagesLoading] = useState(false);
  const [gate1StagesError, setGate1StagesError] = useState<string | null>(null);
  const [gate1Stages, setGate1Stages] = useState<any[] | null>(null);
  const [gate1AlarmsLoading, setGate1AlarmsLoading] = useState(false);
  const [gate1AlarmsError, setGate1AlarmsError] = useState<string | null>(null);
  const [gate1Alarms, setGate1Alarms] = useState<any[] | null>(null);
  const [edgeActionLoading, setEdgeActionLoading] = useState(false);
  const [edgeActionResult, setEdgeActionResult] = useState<any | null>(null);
  const [edgeStageSelected, setEdgeStageSelected] = useState<string | null>(null);
  // Simplified rollback: OTA rollback to known-good from Diagnostics
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [cloudCleanupDialogOpen, setCloudCleanupDialogOpen] = useState(false);
  const [cloudCleanupLoading, setCloudCleanupLoading] = useState(false);
  const [cloudCleanupResult, setCloudCleanupResult] = useState<any | null>(null);
  const [cloudCleanupBefore, setCloudCleanupBefore] = useState<string>(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [cloudCleanupIncludePending, setCloudCleanupIncludePending] = useState<boolean>(true);
  const [expectedPolicyTypes, setExpectedPolicyTypes] = useState<string[]>([]);
  const [expectedPolicyTypesByCamera, setExpectedPolicyTypesByCamera] = useState<Record<string, string[]>>({});

  // F2.3: Evidence delivery state (per incident) - explainability hook
  const [evidenceStateLoading, setEvidenceStateLoading] = useState(false);
  const [evidenceStateError, setEvidenceStateError] = useState<string | null>(null);
  const [evidenceState, setEvidenceState] = useState<any | null>(null);
  const [evidenceStateIncidentId, setEvidenceStateIncidentId] = useState<string>('');

  // Incident upload explainability (edge outbox snapshot by incident_id)
  const [incidentOutboxLoading, setIncidentOutboxLoading] = useState(false);
  const [incidentOutboxError, setIncidentOutboxError] = useState<string | null>(null);
  const [incidentOutbox, setIncidentOutbox] = useState<any | null>(null);
  const [incidentOutboxIncidentId, setIncidentOutboxIncidentId] = useState<string>('');

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState<any | null>(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleResult, setBundleResult] = useState<any | null>(null);

  // Epic 12 Phase A: ops state for consistent degraded/unknown banner
  const [opsStateResult, setOpsStateResult] = useState(() => deriveOpsState({}));
  // Epic 12 Phase B: triage wizard
  const [triageWizardOpen, setTriageWizardOpen] = useState(false);
  useEffect(() => {
    if (triageRequestedFromSearch) setTriageWizardOpen(true);
  }, [triageRequestedFromSearch, deviceId]);

  const [bundleJobId, setBundleJobId] = useState<string | null>(null);
  const [bundleStatusUrl, setBundleStatusUrl] = useState<string | null>(null);
  const [bundleDownloadUrl, setBundleDownloadUrl] = useState<string | null>(null);
  const [bundleDownloaded, setBundleDownloaded] = useState<boolean>(false);
  const [pushZonesLoading, setPushZonesLoading] = useState(false);
  const [pushZonesResult, setPushZonesResult] = useState<any | null>(null);

  // Edge onboarding (deploy_runner: scripts/onboard_edge_unit.sh)
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [onboardResult, setOnboardResult] = useState<any | null>(null);
  const [onboardEdgeHost, setOnboardEdgeHost] = useState<string>('');
  const [onboardCloudHost, setOnboardCloudHost] = useState<string>('');
  const [onboardCloudApiUrl, setOnboardCloudApiUrl] = useState<string>('');
  const [onboardCloudMqttPort, setOnboardCloudMqttPort] = useState<string>('8883');
  const [onboardEdgePassword, setOnboardEdgePassword] = useState<string>('');

  // Edge logs (cloud → edge proxy)
  const [edgeLogsLoading, setEdgeLogsLoading] = useState(false);
  const [edgeLogsError, setEdgeLogsError] = useState<string | null>(null);
  const [edgeLogs, setEdgeLogs] = useState<EdgeLogsResponse | null>(null);
  const [edgeLogProgram, setEdgeLogProgram] = useState<string>('all');
  const [edgeLogLines, setEdgeLogLines] = useState<number>(400);
  const [edgeLogSinceSeconds, setEdgeLogSinceSeconds] = useState<number>(600);
  const [edgeLogJsonOnly, setEdgeLogJsonOnly] = useState<boolean>(true);
  const [edgeLogContains, setEdgeLogContains] = useState<string>('');
  const [edgeLogLevel, setEdgeLogLevel] = useState<string>('');
  const [edgeLogStage, setEdgeLogStage] = useState<string>('');
  const [edgeLogCameraId, setEdgeLogCameraId] = useState<string>('');
  const [edgeLogPipelineId, setEdgeLogPipelineId] = useState<string>('');
  const [edgeLogAutoRefresh, setEdgeLogAutoRefresh] = useState<boolean>(false);
  const [edgeLogRefreshSec, setEdgeLogRefreshSec] = useState<number>(5);
  const [edgeLogView, setEdgeLogView] = useState<'pretty' | 'raw'>('pretty');
  const [edgeLogsSectionExpanded, setEdgeLogsSectionExpanded] = useState(false);

  // Recent actions (audit log) for guardrail visibility
  const [recentActionsLoading, setRecentActionsLoading] = useState(false);
  const [recentActionsError, setRecentActionsError] = useState<string | null>(null);
  const [recentActions, setRecentActions] = useState<any[]>([]);

  /** Pipeline stage chip wall is large; keep a summary visible and expand for full detail. */
  const [pipelineStageChipsExpanded, setPipelineStageChipsExpanded] = useState(false);

  /** Gate 1 persisted tables (stages + alarms) collapsed by default like stage chips. */
  const [gate1MonitorExpanded, setGate1MonitorExpanded] = useState(false);

  // Guardrails audit (edge-side, via cloud proxy)
  const [guardrailsAuditLoading, setGuardrailsAuditLoading] = useState(false);
  const [guardrailsAuditError, setGuardrailsAuditError] = useState<string | null>(null);
  const [guardrailsAudit, setGuardrailsAudit] = useState<any | null>(null);

  // Operator overrides: throttle
  const [throttleOverrideLoading, setThrottleOverrideLoading] = useState(false);
  const [throttleOverrideError, setThrottleOverrideError] = useState<string | null>(null);
  const [throttleOverride, setThrottleOverride] = useState<any | null>(null);
  const [throttleOverrideTtlSec, setThrottleOverrideTtlSec] = useState<number>(900);
  const [throttleOverrideReason, setThrottleOverrideReason] = useState<string>('');

  // Operator overrides: uploaders pause
  const [uploadersPausedLoading, setUploadersPausedLoading] = useState(false);
  const [uploadersPausedError, setUploadersPausedError] = useState<string | null>(null);
  const [uploadersPaused, setUploadersPaused] = useState<any | null>(null);
  const [uploadersPausedTtlSec, setUploadersPausedTtlSec] = useState<number>(900);
  const [uploadersPausedReason, setUploadersPausedReason] = useState<string>('');

  // Operator overrides: list (edge)
  const [overridesListLoading, setOverridesListLoading] = useState(false);
  const [overridesListError, setOverridesListError] = useState<string | null>(null);
  const [overridesList, setOverridesList] = useState<any | null>(null);

  // Coordination status (edge conflicts)
  const [coordinationLoading, setCoordinationLoading] = useState(false);
  const [coordinationError, setCoordinationError] = useState<string | null>(null);
  const [coordinationStatus, setCoordinationStatus] = useState<any | null>(null);

  const capabilityList = useMemo(() => getCapabilitiesFromUser(user), [user]);
  const canReadDiagnostics = canAccessWarehouseSurface(capabilityList, 'diagnostics');
  const canOperate = hasCapability(capabilityList, 'devices:write');
  const canReadAudit = hasCapability(capabilityList, 'audit:read');
  const canManageCloudCleanup = hasCapability(capabilityList, 'admin:*') || hasCapability(capabilityList, 'admin:rbac:manage');

  const openPreflight = () => {
    if (!deviceId) {
      navigate('/preflight');
      return;
    }
    navigate(`/preflight?device=${encodeURIComponent(deviceId)}`);
  };

  const handleRollback = async (params: { reason: string }) => {
    if (!deviceId || !canOperate) return;
    setRollbackLoading(true);
    setEdgeActionResult(null);
    try {
      // 1) Determine rollback target (prefer known-good, else previous)
      const statusRes = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/release/status`);
      const rel = statusRes?.data || {};
      const current = String(rel?.current?.release_id || '').trim();
      const kgr = String(rel?.kgr?.release_id || '').trim();
      const prev = String(rel?.current?.previous_release_id || '').trim();
      const target = kgr || prev;

      if (!target) {
        setEdgeActionResult({ ok: false, error: 'No known-good/previous release available to roll back to.' });
        return;
      }
      if (current && target === current) {
        setEdgeActionResult({ ok: false, error: `Device is already on ${target}.` });
        return;
      }

      // 2) Request rollback via OTA (cloud desired_release → edge agent poll)
      const setRes = await api.put(`/api/v1/devices/${encodeURIComponent(deviceId)}/desired_release`, { desired_release_id: target });
      if (!setRes?.data?.ok) {
        setEdgeActionResult({ ok: false, error: setRes?.data?.error || 'Failed to set desired release for rollback.' });
        return;
      }

      const pollRes = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/release/agent/poll_now`, {
        reason: 'rollback_known_good',
        operator_reason: params.reason || undefined,
      });
      if (!pollRes?.data?.ok) {
        setEdgeActionResult({ ok: false, error: pollRes?.data?.error || 'Rollback trigger failed (agent poll).' });
        return;
      }

      setRollbackDialogOpen(false);
      setEdgeActionResult({ ok: true, target_release_id: target });
      setEdgeStages(null);
      fetchEdgeStageHealth(deviceId);
    } catch (err: any) {
      setEdgeActionResult({ ok: false, error: err?.response?.data?.error || err?.message || 'Rollback failed' });
    } finally {
      setRollbackLoading(false);
    }
  };

  const runCloudIncidentCleanupPreview = async () => {
    if (!canManageCloudCleanup) return;
    setCloudCleanupLoading(true);
    setCloudCleanupResult(null);
    try {
      const res = await api.post('/api/v1/admin/lifecycle/incident_data_cleanup', {
        before: cloudCleanupBefore,
        dry_run: true,
        org_id: orgId || undefined,
        site_id: siteId || undefined,
        include_pending_evidence: cloudCleanupIncludePending,
      });
      setCloudCleanupResult({ ok: true, mode: 'dry_run', ...(res.data || {}) });
    } catch (e: any) {
      setCloudCleanupResult({
        ok: false,
        mode: 'dry_run',
        error: e?.response?.data?.error || e?.message || 'cleanup_preview_failed',
        detail: e?.response?.data?.detail || undefined,
      });
    } finally {
      setCloudCleanupLoading(false);
    }
  };

  const executeCloudIncidentCleanup = async (params: { reason: string }) => {
    if (!canManageCloudCleanup) return;
    setCloudCleanupLoading(true);
    setCloudCleanupResult(null);
    try {
      const res = await api.post('/api/v1/admin/lifecycle/incident_data_cleanup', {
        before: cloudCleanupBefore,
        dry_run: false,
        confirm: 'delete_incident_data_before',
        org_id: orgId || undefined,
        site_id: siteId || undefined,
        include_pending_evidence: cloudCleanupIncludePending,
        reason: params.reason || undefined,
      });
      setCloudCleanupDialogOpen(false);
      setCloudCleanupResult({ ok: true, mode: 'delete', ...(res.data || {}) });
    } catch (e: any) {
      setCloudCleanupResult({
        ok: false,
        mode: 'delete',
        error: e?.response?.data?.error || e?.message || 'cleanup_delete_failed',
        detail: e?.response?.data?.detail || undefined,
        confirm: e?.response?.data?.confirm || undefined,
      });
    } finally {
      setCloudCleanupLoading(false);
    }
  };

  const renderCloudCleanupSummary = (result: any) => {
    const tables = result?.tables && typeof result.tables === 'object' ? result.tables : {};
    const entries = Object.entries(tables);
    if (!entries.length) return null;
    return entries.map(([table, stats]: [string, any]) => {
      const matched = Number(stats?.matched || 0);
      const deleted = Number(stats?.deleted || 0);
      return `${table}: matched ${matched}${result?.dry_run ? '' : `, deleted ${deleted}`}`;
    }).join(' | ');
  };

  const readDeviceFromUrl = useCallback((): string | null => {
    return readDiagnosticsDeviceFromSearch(location.search || '');
  }, [location.search]);

  const setSelectedDeviceId = (id: string | null, opts?: { updateUrl?: boolean }) => {
    deviceIdRef.current = id;
    setDeviceId(id);
    try {
      if (id) localStorage.setItem('ui.last_device_id', id);
    } catch {
      // ignore
    }

    // Best-effort: lock UI scope to the selected device's org/site so incidents/reports don't show empty defaults.
    // IMPORTANT:
    // - Never clobber an operator-selected scope (ui.scope.user_set=true).
    // - Never persist default/default implicitly; treat it as "unset" to avoid silently hiding real data.
    try {
      const userSet = String(localStorage.getItem('ui.scope.user_set') || '').trim() === 'true';
      const did = String(id || '').trim();
      if (did && !userSet) {
        const match = (deviceOptions || []).find((d) => String(d?.device_id || '').trim() === did);
        const orgId = String((match as any)?.org_id || '').trim();
        const siteId = String((match as any)?.site_id || '').trim();
        const isDefaultPair = orgId === 'default' && siteId === 'default';
        if (orgId && siteId && !isDefaultPair) {
          localStorage.setItem('ui.scope.org_id', orgId);
          localStorage.setItem('ui.scope.site_id', siteId);
          // Note: keep ui.scope.user_set unset; this is auto-derived, not operator-chosen.
        }
      }
    } catch {
      // ignore
    }

    if (opts?.updateUrl === false) return;
    try {
      const q = new URLSearchParams(location.search || '');
      if (id) q.set('device', id);
      else q.delete('device');
      const search = q.toString();
      navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  useEffect(() => {
    const next = readDeviceFromUrl();
    if (!next) return;
    if (deviceIdRef.current === next) return;
    deviceIdRef.current = next;
    setDeviceId(next);
  }, [readDeviceFromUrl]);

  useEffect(() => {
    // Manual / collapsed device-specific panels should never show stale data from the prior selection.
    setEdgeLogs(null);
    setEdgeLogsError(null);
    setGuardrailsAudit(null);
    setGuardrailsAuditError(null);
    setGate1Stages(null);
    setGate1StagesError(null);
    setGate1Alarms(null);
    setGate1AlarmsError(null);
    setThrottleOverride(null);
    setThrottleOverrideError(null);
    setUploadersPaused(null);
    setUploadersPausedError(null);
    setOverridesList(null);
    setOverridesListError(null);
    setCoordinationStatus(null);
    setCoordinationError(null);
  }, [deviceId]);

  const fetchDeviceOptions = async () => {
    // Best-effort: dropdown should not block diagnostics if this fails.
    setDeviceOptionsLoading(true);
    setDeviceOptionsError(null);
    const params = orgId && siteId ? { org_id: orgId, site_id: siteId } : undefined;
    try {
      let res: any;
      try {
        res = await api.get('/api/v1/devices/', { params, timeout: 10000 });
      } catch (firstErr: any) {
        // One fast retry for transient network blips.
        if (!firstErr?.response) {
          await sleep(700);
          res = await api.get('/api/v1/devices/', { params, timeout: 10000 });
        } else {
          throw firstErr;
        }
      }
      const items = (res.data?.devices || res.data?.items || []) as any[];
      const out: DeviceOption[] = Array.isArray(items)
        ? items
            .map((d: any) => ({
              device_id: d?.device_id,
              name: d?.name,
              ip_address: d?.ip_address,
              status: d?.status,
              org_id: d?.org_id,
              site_id: d?.site_id,
            }))
            .filter((d) => Boolean(d?.device_id))
        : [];
      setDeviceOptions(out);
      writeDeviceOptionsCache(out);
    } catch (e: any) {
      const cached = readDeviceOptionsCache();
      if (cached.length > 0) {
        setDeviceOptions(cached);
        setDeviceOptionsError(
          `${classifyAxiosError(e).message || 'Failed to load device list'} Showing cached device list.`
        );
      } else {
        setDeviceOptions([]);
        setDeviceOptionsError(classifyAxiosError(e).message || 'Failed to load device list');
      }
    } finally {
      setDeviceOptionsLoading(false);
    }
  };

  const fetchEdgeStageHealth = async (id: string) => {
    if (!id) return;
    setEdgeStagesLoading(true);
    setEdgeStagesError(null);
    setEdgeStagesErrorKind(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/health/stages`);
      if (deviceIdRef.current !== id) return;
      setEdgeStages(res.data || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      const { kind, message } = classifyAxiosError(e);
      setEdgeStagesErrorKind(kind);
      setEdgeStagesError(message);
      // IMPORTANT: keep last good staged health on transient failures so the UI doesn't flap/noise.
    } finally {
      if (deviceIdRef.current === id) setEdgeStagesLoading(false);
    }
  };

  const fetchEdgeHealth = async (id: string) => {
    if (!id) return;
    setEdgeHealthLoading(true);
    setEdgeHealthError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/health`);
      if (deviceIdRef.current !== id) return;
      setEdgeHealth(res.data || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setEdgeHealth(null);
      setEdgeHealthError(classifyAxiosError(e).message || 'edge_health_failed');
    } finally {
      if (deviceIdRef.current === id) setEdgeHealthLoading(false);
    }
  };

  const fetchDeviceHeartbeat = async (id: string) => {
    if (!id) return;
    setDeviceHeartbeatLoading(true);
    setDeviceHeartbeatError(null);
    try {
      const res = await api.get(`/api/v1/devices/${encodeURIComponent(id)}/heartbeat`);
      if (deviceIdRef.current !== id) return;
      setDeviceHeartbeat(res.data || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      // 404 is a valid state: no heartbeat yet (or TTL expired).
      if (e?.response?.status === 404) {
        setDeviceHeartbeat({ ok: false, device_id: id, last_seen: null, age_s: null });
      } else {
        setDeviceHeartbeat(null);
        setDeviceHeartbeatError(classifyAxiosError(e).message || 'device_heartbeat_failed');
      }
    } finally {
      if (deviceIdRef.current === id) setDeviceHeartbeatLoading(false);
    }
  };

  const fetchEdgeFps = async (id: string) => {
    if (!id) return;
    setEdgeFpsLoading(true);
    setEdgeFpsError(null);
    try {
      const safeId = String(id).replace(/"/g, '');
      const extractPromValue = (payload: any): number | null => {
        const result = Array.isArray(payload?.data?.data?.result) ? payload.data.data.result : [];
        const row = result && result.length > 0 ? result[0] : null;
        const v = Array.isArray(row?.value) ? row.value[1] : null;
        const n = v == null ? null : Number(v);
        return Number.isFinite(n as any) ? (n as number) : null;
      };

      // Primary source: Prometheus device FPS in/out from native health/v1.
      let fpsIn: number | null = null;
      let fpsOut: number | null = null;
      try {
        const [resIn, resOut] = await Promise.all([
          api.get('/api/v1/metrics/query/instant', {
            params: { expr: `edge_device_fps_in{device_id="${safeId}"}` },
          }),
          api.get('/api/v1/metrics/query/instant', {
            params: { expr: `edge_device_fps_out{device_id="${safeId}"}` },
          }),
        ]);
        if (deviceIdRef.current !== id) return;
        fpsIn = extractPromValue(resIn);
        fpsOut = extractPromValue(resOut);
      } catch {
        // Ignore and try compatibility paths below.
      }

      // Secondary Prometheus fallback for older/newer single-series emitter naming.
      if (fpsIn == null && fpsOut == null) {
        try {
          const resSecondary = await api.get('/api/v1/metrics/query/instant', {
            params: { expr: `edge_device_fps{device_id="${safeId}"}` },
          });
          if (deviceIdRef.current !== id) return;
          const fallbackFps = extractPromValue(resSecondary);
          fpsIn = fallbackFps;
          fpsOut = fallbackFps;
        } catch {
          // Ignore and try older fallback below.
        }
      }

      if (fpsIn == null && fpsOut == null) {
        try {
          const resSecondary = await api.get('/api/v1/metrics/query/instant', {
            params: { expr: `device_pipeline_fps{device_id="${safeId}"}` },
          });
          if (deviceIdRef.current !== id) return;
          const fallbackFps = extractPromValue(resSecondary);
          fpsIn = fallbackFps;
          fpsOut = fallbackFps;
        } catch {
          // Ignore and try edge metrics fallback below.
        }
      }

      // Final fallback: direct edge metrics proxy (authoritative enough for Operator snapshot).
      if (fpsIn == null && fpsOut == null) {
        try {
          const edgeMetricsRes = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/metrics`);
          if (deviceIdRef.current !== id) return;
          const edgeFpsInRaw = edgeMetricsRes?.data?.fps_in;
          const edgeFpsOutRaw = edgeMetricsRes?.data?.fps_out;
          const edgeFpsRaw = edgeMetricsRes?.data?.fps;
          const nextIn = edgeFpsInRaw == null ? null : Number(edgeFpsInRaw);
          const nextOut = edgeFpsOutRaw == null ? null : Number(edgeFpsOutRaw);
          const nextFallback = edgeFpsRaw == null ? null : Number(edgeFpsRaw);
          if (Number.isFinite(nextIn as any)) fpsIn = nextIn as number;
          if (Number.isFinite(nextOut as any)) fpsOut = nextOut as number;
          if (fpsIn == null && fpsOut == null && Number.isFinite(nextFallback as any)) {
            fpsIn = nextFallback as number;
            fpsOut = nextFallback as number;
          }
        } catch {
          // Leave fps null; error hint path below will explain.
        }
      }

      setEdgeFps((fpsIn == null && fpsOut == null) ? null : { in: fpsIn, out: fpsOut });

      // If FPS is still missing entirely, surface a high-signal operator hint.
      // This typically means the edge is not publishing /mqtt/health/* (cloud has no health telemetry).
      if (fpsIn == null && fpsOut == null) {
        try {
          const expr2 = `edge_device_health_status{device_id="${safeId}"}`;
          const res2 = await api.get('/api/v1/metrics/query/instant', { params: { expr: expr2 } });
          if (deviceIdRef.current !== id) return;
          const r2 = Array.isArray(res2?.data?.data?.result) ? res2.data.data.result : [];
          if (!r2 || r2.length === 0) {
            setEdgeFpsError('no health telemetry (missing /mqtt/health heartbeat)');
          } else {
            setEdgeFpsError('fps missing (health present)');
          }
        } catch {
          setEdgeFpsError('fps missing');
        }
      }
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setEdgeFps(null);
      setEdgeFpsError(classifyAxiosError(e).message || 'edge_fps_failed');
    } finally {
      if (deviceIdRef.current === id) setEdgeFpsLoading(false);
    }
  };

  const fetchLastIncident = async (id: string) => {
    if (!id) return;
    setLastIncidentLoading(true);
    setLastIncidentError(null);
    try {
      const res = await api.get('/api/v1/incidents', { params: { device_id: id, limit: 1, offset: 0 } });
      if (deviceIdRef.current !== id) return;
      const item = Array.isArray(res.data?.items) ? res.data.items[0] : null;
      setLastIncident(item || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setLastIncident(null);
      setLastIncidentError(classifyAxiosError(e).message || 'last_incident_failed');
    } finally {
      if (deviceIdRef.current === id) setLastIncidentLoading(false);
    }
  };

  const fetchIncidentEpisodeRollout = async (windowHours: number = hours) => {
    setEpisodeRolloutLoading(true);
    setEpisodeRolloutError(null);
    try {
      const res = await api.get('/api/v1/incidents/groups/rollout-status', {
        params: { hours: Math.max(24, Number(windowHours || 24)) },
      });
      setEpisodeRollout(res.data || null);
    } catch (e: any) {
      setEpisodeRollout(null);
      setEpisodeRolloutError(classifyAxiosError(e).message || 'incident_episode_rollout_failed');
    } finally {
      setEpisodeRolloutLoading(false);
    }
  };

  const convergeIncidentEpisodeRollout = async () => {
    setEpisodeConvergeLoading(true);
    setEpisodeConvergeError(null);
    setEpisodeConvergeResult(null);
    try {
      const res = await api.post('/api/v1/incidents/groups/converge', {
        hours: Math.max(24, Number(hours || 24)),
        limit: 200,
      });
      setEpisodeConvergeResult(res.data || null);
      await fetchIncidentEpisodeRollout(hours);
    } catch (e: any) {
      setEpisodeConvergeError(classifyAxiosError(e).message || 'incident_episode_converge_failed');
    } finally {
      setEpisodeConvergeLoading(false);
    }
  };

  const fetchDrbStatus = async (id: string) => {
    if (!id) return;
    setDrbStatusLoading(true);
    setDrbStatusError(null);
    try {
      const res = await api.get(`/api/v1/devices/${encodeURIComponent(id)}/drb`);
      if (deviceIdRef.current !== id) return;
      setDrbStatus(res.data || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setDrbStatus(null);
      setDrbStatusError(classifyAxiosError(e).message || 'drb_status_failed');
    } finally {
      if (deviceIdRef.current === id) setDrbStatusLoading(false);
    }
  };

  const _fetchConvergence = async (id: string) => {
    if (!id) return;
    setConvergenceLoading(true);
    setConvergenceError(null);
    try {
      const res = await api.post('/api/v1/deployments/clarity/convergence_batch', { device_ids: [id], limit: 1 });
      if (deviceIdRef.current !== id) return;
      const item = Array.isArray(res.data?.items) ? res.data.items[0] : null;
      setConvergence(item || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setConvergence(null);
      setConvergenceError(classifyAxiosError(e).message || 'convergence_failed');
    } finally {
      if (deviceIdRef.current === id) setConvergenceLoading(false);
    }
  };

  const fetchConfigDrift = async (id: string) => {
    if (!id) return;
    setConfigDriftLoading(true);
    setConfigDriftError(null);
    try {
      const data = await getConfigDrift(id, { timeout_ms: 5000 });
      if (deviceIdRef.current !== id) return;
      setConfigDrift(data);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setConfigDrift(null);
      setConfigDriftError(classifyAxiosError(e).message || 'config_drift_failed');
    } finally {
      if (deviceIdRef.current === id) setConfigDriftLoading(false);
    }
  };

  const handleConvergeDrift = async () => {
    if (!deviceId || !configDrift?.types) return;
    setConvergeLoading(true);
    setConvergeError(null);
    const types = configDrift.types;
    const policiesDrifted = (types.policies as any)?.status === 'drifted' || (types.policies as any)?.status === 'pending';
    const zonesDrifted = (types.zones as any)?.status === 'drifted' || (types.zones as any)?.status === 'pending';
    const drbDrifted = (types.drb as any)?.status === 'drifted' || (types.drb as any)?.status === 'pending';
    const fcpDrifted = (types.fcp as any)?.status === 'drifted' || (types.fcp as any)?.status === 'pending';
    const match = (deviceOptions || []).find((d) => String(d?.device_id || '').trim() === deviceId) as { org_id?: string; site_id?: string } | undefined;
    const zonesPushBody: Record<string, string> = { device_id: deviceId };
    if (match?.org_id) zonesPushBody.org_id = match.org_id;
    if (match?.site_id) zonesPushBody.site_id = match.site_id;
    try {
      let runtimeStatus: any = null;
      try {
        runtimeStatus = await runtimeBlueprintsApi.edgeRuntimeStatus(deviceId);
      } catch (_) {
        runtimeStatus = null;
      }
      const runtimeConfigVersions =
        runtimeStatus?.config_versions && typeof runtimeStatus.config_versions === 'object'
          ? runtimeStatus.config_versions
          : {};
      const runtimeArtifacts =
        runtimeStatus?.artifacts && typeof runtimeStatus.artifacts === 'object'
          ? runtimeStatus.artifacts
          : {};
      const desiredPoliciesHash = String((types.policies as any)?.desired?.bundle_hash || '').trim();
      const desiredZonesHash = String((types.zones as any)?.desired?.bundle_hash || '').trim();
      const zonesCameraId = String((types.zones as any)?.camera_id || '').trim();
      const runtimePoliciesHash = String(runtimeConfigVersions?.policies?.bundle_hash || '').trim();
      const runtimePoliciesArtifactKnown = Object.prototype.hasOwnProperty.call(runtimeArtifacts, 'effective_policies');
      const runtimePoliciesArtifactPresent = Boolean(runtimeArtifacts?.effective_policies?.path);
      const runtimeZonesHash = String(runtimeConfigVersions?.zones?.[zonesCameraId]?.bundle_hash || '').trim();
      const runtimeZonesArtifactKnown =
        runtimeArtifacts?.effective_zones && typeof runtimeArtifacts.effective_zones === 'object';
      const runtimeZonesArtifactPresent = Boolean(runtimeArtifacts?.effective_zones?.[zonesCameraId]?.path);
      const policiesNeedRecovery =
        policiesDrifted ||
        (Boolean(desiredPoliciesHash) && !runtimePoliciesHash) ||
        (Boolean(desiredPoliciesHash) && runtimePoliciesArtifactKnown && !runtimePoliciesArtifactPresent);
      const zonesNeedRecovery =
        zonesDrifted ||
        (Boolean(desiredZonesHash && zonesCameraId) && !runtimeZonesHash) ||
        (Boolean(desiredZonesHash && zonesCameraId) && runtimeZonesArtifactKnown && !runtimeZonesArtifactPresent);
      if (!policiesNeedRecovery && !zonesNeedRecovery && !drbDrifted && !fcpDrifted) {
        return;
      }

      await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/desired_state/sync`, {});
      if (drbDrifted) {
        await runtimeBlueprintsApi.republishClarity({ edge_device_id: deviceId, config_type: 'drb' });
      }
      if (fcpDrifted) {
        await runtimeBlueprintsApi.republishClarity({ edge_device_id: deviceId, config_type: 'fcp' });
      }
      if (policiesNeedRecovery) {
        await runtimeBlueprintsApi.republishClarity({ edge_device_id: deviceId, config_type: 'policies' });
      }
      if (zonesNeedRecovery) {
        // Try HTTP inject first (bypasses MQTT when cloud can reach edge directly)
        try {
          const injectRes = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/config/zones/inject`, {});
          if ((injectRes.data as any)?.injected > 0) {
            // HTTP inject succeeded for at least one camera
          } else {
            // Fall back to MQTT
            await api.post('/api/v1/zones/push', zonesPushBody);
            try {
              await runtimeBlueprintsApi.republishZonesAll(deviceId);
            } catch (_) {
              // Ignore: republish is best-effort
            }
          }
        } catch (_) {
          // HTTP inject failed (edge unreachable); fall back to MQTT
          await api.post('/api/v1/zones/push', zonesPushBody);
          try {
            await runtimeBlueprintsApi.republishZonesAll(deviceId);
          } catch (__) {
            // Ignore
          }
        }
      }
      if (deviceIdRef.current === deviceId) {
        await new Promise((r) => setTimeout(r, 2000));
        await fetchConfigDrift(deviceId);
      }
    } catch (e: any) {
      if (deviceIdRef.current === deviceId) setConvergeError(classifyAxiosError(e).message || 'converge_failed');
    } finally {
      if (deviceIdRef.current === deviceId) setConvergeLoading(false);
    }
  };

  const staleThresh = configDrift?.stale_threshold_minutes ?? 5;
  const polStale =
    (configDrift?.types?.policies as any)?.stale_minutes != null &&
    (configDrift?.types?.policies as any).stale_minutes > staleThresh &&
    ((configDrift?.types?.policies as any).status === 'converged' || (configDrift?.types?.policies as any).status === 'drifted');
  const zonesStale =
    (configDrift?.types?.zones as any)?.stale_minutes != null &&
    (configDrift?.types?.zones as any).stale_minutes > staleThresh &&
    ((configDrift?.types?.zones as any).status === 'converged' || (configDrift?.types?.zones as any).status === 'drifted');
  const drbStale =
    (configDrift?.types?.drb as any)?.stale_minutes != null &&
    (configDrift?.types?.drb as any).stale_minutes > staleThresh &&
    ((configDrift?.types?.drb as any).status === 'converged' || (configDrift?.types?.drb as any).status === 'drifted');
  const fcpStale =
    (configDrift?.types?.fcp as any)?.stale_minutes != null &&
    (configDrift?.types?.fcp as any).stale_minutes > staleThresh &&
    ((configDrift?.types?.fcp as any).status === 'converged' || (configDrift?.types?.fcp as any).status === 'drifted');
  const hasDriftedPoliciesOrZones =
    configDrift?.types &&
    (((configDrift.types.policies as any)?.status === 'drifted' || (configDrift.types.policies as any)?.status === 'pending') ||
      ((configDrift.types.zones as any)?.status === 'drifted' || (configDrift.types.zones as any)?.status === 'pending') ||
      ((configDrift.types.drb as any)?.status === 'drifted' || (configDrift.types.drb as any)?.status === 'pending') ||
      ((configDrift.types.fcp as any)?.status === 'drifted' || (configDrift.types.fcp as any)?.status === 'pending') ||
      polStale ||
      zonesStale ||
      drbStale ||
      fcpStale);

  const fetchDeviceDiagnosis = async (id: string) => {
    if (!id) return;
    setDeviceDiagnosisError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/diagnosis`);
      if (deviceIdRef.current !== id) return;
      setDeviceDiagnosis(res.data || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setDeviceDiagnosis(null);
      setDeviceDiagnosisError(e?.response?.data?.error || e?.message || 'diagnosis_failed');
    }
  };

  const fetchBringupLatest = async (id: string) => {
    if (!id) return;
    try {
      const res = await api.get(
        `/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/commission_one_click/latest`,
      );
      if (deviceIdRef.current !== id) return;
      setBringupLatest(res.data || null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      if (e?.response?.status === 404) {
        setBringupLatest(null);
        return;
      }
      setBringupLatest(null);
    }
  };

  const fetchGate1Stages = async (id: string) => {
    if (!id) return;
    setGate1StagesLoading(true);
    setGate1StagesError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/gate1/stages`);
      if (deviceIdRef.current !== id) return;
      setGate1Stages(Array.isArray((res.data as any)?.items) ? (res.data as any).items : null);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setGate1Stages(null);
      setGate1StagesError(e?.response?.data?.error || e?.message || 'gate1_stages_failed');
    } finally {
      if (deviceIdRef.current === id) setGate1StagesLoading(false);
    }
  };

  const fetchGate1Alarms = async (id: string) => {
    if (!id) return;
    setGate1AlarmsLoading(true);
    setGate1AlarmsError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/gate1/alarms`, { params: { status: 'active' } });
      if (deviceIdRef.current !== id) return;
      setGate1Alarms(Array.isArray((res.data as any)?.items) ? (res.data as any).items : []);
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setGate1Alarms([]);
      setGate1AlarmsError(e?.response?.data?.error || e?.message || 'gate1_alarms_failed');
    } finally {
      if (deviceIdRef.current === id) setGate1AlarmsLoading(false);
    }
  };

  const fetchEvidenceState = async (id: string, incidentId: string) => {
    if (!id || !incidentId) return;
    setEvidenceStateLoading(true);
    setEvidenceStateError(null);
    try {
      const res = await api.get(
        `/api/v1/pipeline/devices/${encodeURIComponent(id)}/incidents/${encodeURIComponent(incidentId)}/evidence_state`
      );
      setEvidenceState(res.data || null);
    } catch (e: any) {
      setEvidenceState(null);
      setEvidenceStateError(e?.response?.data?.error || e?.message || 'evidence_state_failed');
    } finally {
      setEvidenceStateLoading(false);
    }
  };

  const fetchIncidentOutbox = async (id: string, incidentId: string) => {
    if (!id || !incidentId) return;
    setIncidentOutboxLoading(true);
    setIncidentOutboxError(null);
    try {
      const res = await api.get(
        `/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/outbox/incident/${encodeURIComponent(incidentId)}`,
        { params: { limit: 120 } }
      );
      setIncidentOutbox(res.data || null);
    } catch (e: any) {
      setIncidentOutbox(null);
      setIncidentOutboxError(e?.response?.data?.error || e?.message || 'incident_outbox_failed');
    } finally {
      setIncidentOutboxLoading(false);
    }
  };

  const ackGate1Alarm = async (alarmId: string) => {
    if (!deviceId || !alarmId) return;
    try {
      await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/gate1/alarms/${encodeURIComponent(alarmId)}/ack`, {});
      fetchGate1Alarms(deviceId);
    } catch (e: any) {
      setGate1AlarmsError(e?.response?.data?.error || e?.message || 'gate1_alarm_ack_failed');
    }
  };

  const fetchRecentActions = async (id: string) => {
    if (!id) return;
    setRecentActionsLoading(true);
    setRecentActionsError(null);
    try {
      // NOTE: audit API is mounted with a trailing slash route ('/'), so call the canonical
      // path to avoid a 308 redirect (which can trigger CORS issues behind misconfigured proxies).
      const res = await api.get('/api/v1/audit/', {
        params: {
          resource_type: 'PIPELINE_CONTROL',
          resource_id: id,
          per_page: 10,
          page: 1,
        },
      });
      if (deviceIdRef.current !== id) return;
      setRecentActions((res.data?.logs || []).slice(0, 10));
    } catch (e: any) {
      if (deviceIdRef.current !== id) return;
      setRecentActions([]);
      setRecentActionsError(classifyAxiosError(e).message || 'audit_fetch_failed');
    } finally {
      if (deviceIdRef.current === id) setRecentActionsLoading(false);
    }
  };

  const syncDesiredStateNow = async () => {
    if (!deviceId) return;
    setEdgeActionLoading(true);
    setEdgeActionResult(null);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/desired_state/sync`, {});
      setEdgeActionResult({ ok: true, action: 'sync_desired_state', result: res.data });
      fetchEdgeStageHealth(deviceId);
      fetchDeviceDiagnosis(deviceId);
      fetchGate1Stages(deviceId);
      fetchGate1Alarms(deviceId);
    } catch (e: any) {
      setEdgeActionResult({ ok: false, action: 'sync_desired_state', error: e?.response?.data?.error || e?.message || 'sync_failed' });
    } finally {
      setEdgeActionLoading(false);
    }
  };

  const restartUploadersNow = async () => {
    if (!deviceId) return;
    setEdgeActionLoading(true);
    setEdgeActionResult(null);
    try {
      const url = `/api/v1/pipeline/devices/${deviceId}/edge/services/restart`;
      const basePayload: any = { programs: ['incident_uploader', 'evidence_uploader'] };
      const tryOnce = async (payload: any) => api.post(url, payload);

      try {
        // Guardrailed action: supervisor restart requires an explicit confirm token.
        // Provide it up front to avoid a confusing "Edge returned 400" UX.
        const res = await tryOnce({ ...basePayload, confirm: 'restart_supervisor' });
        setEdgeActionResult({ ok: true, action: 'restart_uploaders', result: res.data });
      } catch (e1: any) {
        // Backward/compat: some edge versions return confirm_required as JSON embedded in `detail`.
        // If so, extract the confirm token and retry once.
        try {
          const detailRaw = e1?.response?.data?.detail;
          const detail = typeof detailRaw === 'string' ? detailRaw : '';
          const inner = detail ? JSON.parse(detail) : null;
          const confirmToken = inner && inner.error === 'confirm_required' ? String(inner.confirm || '').trim() : '';
          if (confirmToken) {
            const res2 = await tryOnce({ ...basePayload, confirm: confirmToken });
            setEdgeActionResult({ ok: true, action: 'restart_uploaders', result: res2.data });
          } else {
            throw e1;
          }
        } catch {
          throw e1;
        }
      }
      fetchEdgeStageHealth(deviceId);
      fetchDeviceDiagnosis(deviceId);
      fetchGate1Stages(deviceId);
      fetchGate1Alarms(deviceId);
    } catch (e: any) {
      setEdgeActionResult({ ok: false, action: 'restart_uploaders', error: e?.response?.data?.error || e?.message || 'restart_failed' });
    } finally {
      setEdgeActionLoading(false);
    }
  };

  const runLogMaintenanceNow = async () => {
    if (!deviceId) return;
    setEdgeActionLoading(true);
    setEdgeActionResult(null);
    try {
      const url = `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/logs/maintenance`;
      const basePayload: any = { confirm: 'run_log_maintenance' };
      const tryOnce = async (payload: any) => api.post(url, payload);
      try {
        const res = await tryOnce(basePayload);
        setEdgeActionResult({ ok: true, action: 'run_log_maintenance', result: res.data });
      } catch (e1: any) {
        // Backward/compat: some edge versions return confirm_required as JSON embedded in `detail`.
        // If so, extract the confirm token and retry once.
        try {
          const detailRaw = e1?.response?.data?.detail;
          const detail = typeof detailRaw === 'string' ? detailRaw : '';
          const inner = detail ? JSON.parse(detail) : null;
          const confirmToken = inner && inner.error === 'confirm_required' ? String(inner.confirm || '').trim() : '';
          if (confirmToken) {
            const res2 = await tryOnce({ ...basePayload, confirm: confirmToken });
            setEdgeActionResult({ ok: true, action: 'run_log_maintenance', result: res2.data });
          } else {
            throw e1;
          }
        } catch {
          throw e1;
        }
      }
      fetchEdgeStageHealth(deviceId);
      fetchDeviceDiagnosis(deviceId);
      fetchGate1Stages(deviceId);
      fetchGate1Alarms(deviceId);
    } catch (e: any) {
      setEdgeActionResult({ ok: false, action: 'run_log_maintenance', error: e?.response?.data?.error || e?.message || 'log_maintenance_failed' });
    } finally {
      setEdgeActionLoading(false);
    }
  };

  const retryFailedEvidenceOutboxNow = async () => {
    if (!deviceId) return;
    setEdgeActionLoading(true);
    setEdgeActionResult(null);
    try {
      const url = `/api/v1/pipeline/devices/${deviceId}/edge/outbox/reset_failed`;
      const basePayload: any = { job_type: 'EVIDENCE_UPLOAD', limit: 200, reset_attempts: true };
      const tryOnce = async (payload: any) => api.post(url, payload);

      try {
        // Guardrailed action: outbox reset requires an explicit confirm token.
        const res = await tryOnce({ ...basePayload, confirm: 'reset_failed' });
        setEdgeActionResult({ ok: true, action: 'reset_failed_outbox', result: res.data });
      } catch (e1: any) {
        // Backward/compat: some edge versions return confirm_required as JSON embedded in `detail`.
        // If so, extract the confirm token and retry once.
        try {
          const detailRaw = e1?.response?.data?.detail;
          const detail = typeof detailRaw === 'string' ? detailRaw : '';
          const inner = detail ? JSON.parse(detail) : null;
          const confirmToken = inner && inner.error === 'confirm_required' ? String(inner.confirm || '').trim() : '';
          if (confirmToken) {
            const res2 = await tryOnce({ ...basePayload, confirm: confirmToken });
            setEdgeActionResult({ ok: true, action: 'reset_failed_outbox', result: res2.data });
          } else {
            throw e1;
          }
        } catch {
          throw e1;
        }
      }
      fetchEdgeStageHealth(deviceId);
      fetchDeviceDiagnosis(deviceId);
    } catch (e: any) {
      setEdgeActionResult({ ok: false, action: 'reset_failed_outbox', error: e?.response?.data?.error || e?.message || 'reset_failed' });
    } finally {
      setEdgeActionLoading(false);
    }
  };

  const fetchDiagnostics = async (overrideDeviceId?: string | null) => {
    const reqId = diagnosticsReqRef.current + 1;
    diagnosticsReqRef.current = reqId;
    setLoading(true);
    setError(null);
    try {
      // Prefer UI scope; if missing and a device is selected, infer scope from that device's latest incident.
      // IMPORTANT: do NOT fall back to default/default implicitly. That points at the wrong site and hides real data.
      const scope = ensureUiScope();
      let orgId = String(scope.orgId || '').trim();
      let siteId = String(scope.siteId || '').trim();
      const userSet = (() => {
        try {
          return String(localStorage.getItem('ui.scope.user_set') || '').trim() === 'true';
        } catch {
          return false;
        }
      })();

      // Best-effort: preselect a device from URL/dropdown/last-selection so we can infer scope before site-scoped fetches.
      // (Use *_pre names to avoid shadowing later device-selection locals in this function.)
      const override_pre = String(overrideDeviceId || '').trim();
      const urlDevice_pre = readDeviceFromUrl();
      let lastDevice_pre: string | null = null;
      try {
        lastDevice_pre = String(localStorage.getItem('ui.last_device_id') || '').trim() || null;
      } catch {
        lastDevice_pre = null;
      }
      const preselectedDeviceId = (override_pre || urlDevice_pre || lastDevice_pre || '').trim();
      if (preselectedDeviceId && deviceIdRef.current !== preselectedDeviceId) {
        deviceIdRef.current = preselectedDeviceId;
        setDeviceId(preselectedDeviceId);
      }

      const isDefaultPair = orgId === 'default' && siteId === 'default';
      const scopeUsable = Boolean(orgId && siteId && (!isDefaultPair || userSet));

      if (!scopeUsable && preselectedDeviceId) {
        try {
          const res = await api.get('/api/v1/incidents', { params: { device_id: preselectedDeviceId, limit: 1, offset: 0 } });
          const item = Array.isArray(res.data?.items) ? res.data.items[0] : null;
          const orgInf = String(item?.org_id || '').trim();
          const siteInf = String(item?.site_id || '').trim();
          const isDefaultInf = orgInf === 'default' && siteInf === 'default';
          if (orgInf && siteInf && !isDefaultInf) {
            orgId = orgInf;
            siteId = siteInf;
            // Persist inferred scope only when the operator hasn't explicitly set one.
            if (!userSet) {
              try {
                localStorage.setItem('ui.scope.org_id', orgInf);
                localStorage.setItem('ui.scope.site_id', siteInf);
              } catch {
                // ignore
              }
            }
          }
        } catch {
          // ignore (scope inference is best-effort)
        }
      }

      if (preselectedDeviceId) {
        try {
          const summaryRes = await withTimeout(
            api.get(`/api/v1/diagnostics/devices/${encodeURIComponent(preselectedDeviceId)}/summary`, {
              params: { hours },
              timeout: PAGE_TIMEOUT_MS,
            }),
            'Device diagnostics'
          );
          if (reqId !== diagnosticsReqRef.current) return;

          const payload = summaryRes.data || {};
          const summaryReport = payload?.report;
          const summaryDevice = payload?.device && typeof payload.device === 'object' ? payload.device : {};
          const activeDeviceId = String(summaryDevice?.device_id || preselectedDeviceId).trim();
          const summaryOrgId = String(summaryDevice?.org_id || '').trim();
          const summarySiteId = String(summaryDevice?.site_id || '').trim();
          const summaryExpected = Array.isArray(payload?.expected_policy_types)
            ? payload.expected_policy_types.map((v: any) => String(v)).sort()
            : [];
          const summaryExpectedByCamera = payload?.expected_policy_types_by_camera
            && typeof payload.expected_policy_types_by_camera === 'object'
            ? payload.expected_policy_types_by_camera
            : {};

          if (summaryOrgId && summarySiteId && !userSet) {
            try {
              localStorage.setItem('ui.scope.org_id', summaryOrgId);
              localStorage.setItem('ui.scope.site_id', summarySiteId);
            } catch {
              // ignore
            }
          }

          if (activeDeviceId) {
            setCameraDeviceCandidates([activeDeviceId]);
            setSelectedDeviceId(activeDeviceId, { updateUrl: true });
          }
          setExpectedPolicyTypes(summaryExpected);
          setExpectedPolicyTypesByCamera(summaryExpectedByCamera);

          if (summaryReport && Array.isArray(summaryReport.cameras) && summaryReport.cameras.length > 0) {
            setReport(summaryReport as DiagnosticReport);

            if (activeDeviceId) {
              const id = activeDeviceId;
              fetchEdgeStageHealth(id);
              fetchEdgeHealth(id);
              fetchDeviceHeartbeat(id);
              fetchEdgeFps(id);
              scheduleDeferredWork(() => {
                if (reqId !== diagnosticsReqRef.current) return;
                if (deviceIdRef.current !== id) return;
                fetchLastIncident(id);
                fetchDrbStatus(id);
                fetchConfigDrift(id);
                fetchDeviceDiagnosis(id);
                fetchBringupLatest(id);
                fetchRecentActions(id);
              });
            }
            return;
          }
          if (summaryReport && Array.isArray(summaryReport.cameras)) {
            console.warn(
              'Device diagnostics summary returned no cameras; falling back to registry/site bootstrap.'
            );
          }
        } catch (e) {
          console.warn('Could not load device diagnostics summary; falling back to site bootstrap:', e);
        }
      }

      if (!siteId) {
        throw new Error('org/site scope not set (use Incidents → scope button to select the correct site)');
      }

      const camerasRequest = preselectedDeviceId
        ? api.get(`/api/v1/registry/devices/${encodeURIComponent(preselectedDeviceId)}/cameras`)
        : api.get(`/api/v1/sites/${encodeURIComponent(siteId)}/cameras`, { timeout: PAGE_TIMEOUT_MS });

      // Fetch all required data (site-scoped unless we already know the device and can narrow the camera/zones path).
      const [camerasRes, policiesRes, zonesRes, incidentsRes] = await Promise.all([
        withTimeout(
          camerasRequest,
          preselectedDeviceId ? 'Device cameras' : 'Cameras'
        ),
        withTimeout(api.get(`/api/v1/sites/${encodeURIComponent(siteId)}/policies`, { timeout: PAGE_TIMEOUT_MS }), 'Policies'),
        // Zones CRUD/list lives at /api/v1/zones (site-agnostic) in this repo.
        // (Phase D endpoints under /api/v1/sites/<site_id>/zones/* are deploy/validate only.)
        withTimeout(api.get(`/api/v1/zones`, {
          params: {
            include_disabled: true,
            ...(preselectedDeviceId ? { device_id: preselectedDeviceId } : {}),
          },
          timeout: PAGE_TIMEOUT_MS,
        }), 'Zones'),
        withTimeout(api.get('/api/v1/incidents', {
          params: {
            // Prefer explicit scope when known.
            ...(orgId ? { org_id: orgId } : {}),
            site_id: siteId,
            // Keep diagnostics fast: when a specific device is selected, only pull that device's incidents.
            ...(preselectedDeviceId ? { device_id: preselectedDeviceId } : {}),
            // Server caps at 500; avoid overspecifying and duplicating work.
            limit: 500,
          },
          timeout: PAGE_TIMEOUT_MS,
        }), 'Incidents'),
      ]);
      if (reqId !== diagnosticsReqRef.current) return;

      const camerasRaw = Array.isArray(camerasRes.data?.items) ? camerasRes.data.items : [];
      const camerasLoadedFromRegistry = Boolean(preselectedDeviceId);
      const camerasAll = camerasLoadedFromRegistry
        ? camerasRaw.map((c: any) => ({
            ...c,
            edge_device_id: preselectedDeviceId,
            device_id: preselectedDeviceId,
            enabled: c?.enabled ?? (c?.health?.status !== 'disabled'),
          }))
        : camerasRaw;
      // Policies API returns { policies: [...] } (not { items: [...] }).
      const allPolicies = (policiesRes.data.items || policiesRes.data.policies || []).filter(Boolean);
      const zones = zonesRes.data.zones || zonesRes.data.items || [];
      
      // Filter incidents by time
      const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000);
      const incidents = (incidentsRes.data.items || []).filter((inc: any) => {
        const ts = inc.ts_start || inc.created_at;
        if (!ts) return false;
        return new Date(ts) >= cutoff;
      });

      // Try to get edge status (optional, may fail)
      let edgeStatus: Record<string, any> = {};
      // Camera objects in this repo use `edge_device_id` (Phase C-lite). Some legacy APIs may also return `device_id`.
      const deviceIds = new Set(
        camerasAll
          .map((c: any) => c.edge_device_id || c.device_id)
          .filter(Boolean)
      );
      if (preselectedDeviceId) deviceIds.add(preselectedDeviceId);

      if (reqId !== diagnosticsReqRef.current) return;
      setCameraDeviceCandidates(Array.from(deviceIds).map(String).sort());

      // Device selection precedence:
      // 1) explicit override (dropdown)
      // 2) URL query (?device=...)
      // 3) last selected (localStorage)
      // 4) first known device (from /devices list or cameras)
      const override = String(overrideDeviceId || '').trim();
      const urlDevice = readDeviceFromUrl();
      let lastDevice: string | null = null;
      try {
        lastDevice = String(localStorage.getItem('ui.last_device_id') || '').trim() || null;
      } catch {
        lastDevice = null;
      }

      const knownIds = new Set<string>();
      (deviceOptions || []).forEach((d) => {
        if (d?.device_id) knownIds.add(String(d.device_id));
      });
      deviceIds.forEach((d) => knownIds.add(String(d)));
      const knownList = Array.from(knownIds).sort();

      const selectedDeviceId =
        override ||
        urlDevice ||
        (lastDevice && knownIds.has(lastDevice) ? lastDevice : '') ||
        (knownList.length ? knownList[0] : '');

      const activeDeviceId = selectedDeviceId ? String(selectedDeviceId) : null;

      if (reqId !== diagnosticsReqRef.current) return;
      if (activeDeviceId) {
        setSelectedDeviceId(activeDeviceId, { updateUrl: true });
        try {
          const edgeRes = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(activeDeviceId)}/edge/pipelines/cameras`);
          const edgeCameras = edgeRes.data.cameras || [];
          edgeCameras.forEach((cam: any) => {
            if (cam.camera_id) {
              edgeStatus[cam.camera_id] = cam;
            }
          });
        } catch (e) {
          // Edge status is optional
          console.warn('Could not fetch edge status:', e);
        }
        // Device-scoped panels load once the main diagnostic report is ready (see end of fetchDiagnostics)
        // so we do not contend with cameras/policies/incidents on the critical path.
      } else {
        setSelectedDeviceId(null, { updateUrl: true });
        setEdgeStages(null);
        setEdgeStagesError(null);
        setDeviceDiagnosis(null);
        setDeviceDiagnosisError(null);
        setRecentActions([]);
        setRecentActionsError(null);
      }

      // Single source of truth for selected device cameras:
      // use registry device-camera endpoint (authoritative binding), then fall back to site list.
      let cameras =
        activeDeviceId
          ? (camerasAll || []).filter((c: any) => (c.edge_device_id || c.device_id) === activeDeviceId)
          : camerasAll;
      if (activeDeviceId && !(camerasLoadedFromRegistry && activeDeviceId === preselectedDeviceId)) {
        try {
          const regRes = await api.get(`/api/v1/registry/devices/${encodeURIComponent(activeDeviceId)}/cameras`);
          if (reqId !== diagnosticsReqRef.current) return;
          const items = Array.isArray(regRes?.data?.items) ? regRes.data.items : [];
          cameras = items.map((c: any) => ({
            ...c,
            edge_device_id: activeDeviceId,
            device_id: activeDeviceId,
            enabled: c?.health?.status !== 'disabled',
          }));
        } catch {
          // keep fallback cameras list
        }
      }

      const zonesScoped =
        activeDeviceId
          ? (zones || []).filter(
              (z: any) => String(z?.device_id || z?.edge_device_id || '').trim() === activeDeviceId
            )
          : zones;
      const policiesScoped = (allPolicies || []).filter((p: any) => {
        if (!activeDeviceId) return true;
        const target = p?.target && typeof p.target === 'object' ? p.target : {};
        const did = String(target?.edge_device_id || p?.edge_device_id || '').trim();
        // Enforce deterministic target binding in diagnostics when a device is selected.
        return did === activeDeviceId;
      });
      const enabledPoliciesScoped = policiesScoped.filter((p: any) => p?.enabled !== false);

      // Analyze camera-policy mapping
      const zonesByCamera: Record<string, any[]> = {};
      zonesScoped.forEach((zone: any) => {
        const camId = zone.camera_id;
        if (camId) {
          if (!zonesByCamera[camId]) zonesByCamera[camId] = [];
          zonesByCamera[camId].push(zone);
        }
      });

      const policiesByCamera: Record<string, any[]> = {};
      enabledPoliciesScoped.forEach((policy: any) => {
        const scope = policy.scope || {};
        const cameraIds = scope.camera_ids || [];
        const zoneIds = scope.zone_ids || [];
        const zoneTags = scope.zone_tags || [];

        // Find cameras via zone_ids
        const camerasViaZones = new Set<string>();
        zoneIds.forEach((zoneId: string) => {
          zonesScoped.forEach((zone: any) => {
            if (zone.zone_id === zoneId || zone.id === zoneId) {
              if (zone.camera_id) camerasViaZones.add(zone.camera_id);
            }
          });
        });

        // Find cameras via zone_tags
        zoneTags.forEach((tag: string) => {
          zonesScoped.forEach((zone: any) => {
            const zoneTagsList = zone.tags || [];
            if (zoneTagsList.includes(tag) && zone.camera_id) {
              camerasViaZones.add(zone.camera_id);
            }
          });
        });

        // Combine explicit camera_ids with cameras from zones.
        // IMPORTANT: avoid spreading/iterating Set here because the dashboard TS target
        // does not enable --downlevelIteration (CRA build), which breaks compilation.
        const allCameras = new Set<string>(cameraIds as string[]);
        camerasViaZones.forEach((cid) => allCameras.add(cid));

        // If no explicit cameras and no zones, policy applies to all (site-wide)
        if (allCameras.size === 0 && zoneIds.length === 0 && zoneTags.length === 0) {
          cameras.forEach((cam: any) => {
            if (cam.camera_id) allCameras.add(cam.camera_id);
          });
        }

        allCameras.forEach((camId) => {
          if (!policiesByCamera[camId]) policiesByCamera[camId] = [];
          policiesByCamera[camId].push(policy);
        });
      });

      // Expected policy set contract (diagnosis): what cloud believes should be active on this device.
      // We derive expected policy types from enabled policies that map to cameras on this device.
      const deviceCameraIds: string[] = (cameras || []).map((c: any) => c.camera_id).filter(Boolean);

      const expByCam: Record<string, Set<string>> = {};
      deviceCameraIds.forEach((cid) => (expByCam[cid] = new Set<string>()));
      deviceCameraIds.forEach((cid) => {
        (policiesByCamera[cid] || []).forEach((p: any) => {
          const t = p?.policy_type;
          if (t) expByCam[cid].add(String(t));
        });
      });
      const expOverall = new Set<string>();
      Object.values(expByCam).forEach((s) => s.forEach((t) => expOverall.add(t)));
      setExpectedPolicyTypes(Array.from(expOverall).sort());
      const expByCamOut: Record<string, string[]> = {};
      Object.keys(expByCam).forEach((cid) => (expByCamOut[cid] = Array.from(expByCam[cid]).sort()));
      setExpectedPolicyTypesByCamera(expByCamOut);

      // Count incidents by camera
      const incidentsByCamera: Record<string, number> = {};
      incidents.forEach((inc: any) => {
        const camId = inc.camera_id || inc.envelope_metadata?.camera_id;
        if (camId) {
          incidentsByCamera[camId] = (incidentsByCamera[camId] || 0) + 1;
        }
      });

      // Build diagnostic report
      const diagnosticReport: DiagnosticReport = {
        summary: {
          total_cameras: cameras.length,
          total_policies: enabledPoliciesScoped.length,
          total_policies_all: policiesScoped.length,
          total_zones: zonesScoped.length,
          total_incidents_24h: incidents.length,
        },
        cameras: cameras.map((camera: any) => {
          const camId = camera.camera_id;
          if (!camId) return null;

          // Camera list API in this repo does not always provide a `source` object.
          // Infer type from stable fields.
          const rtspUrl = (camera.rtsp_url || camera.rtspUrl || '') as string;
          const source = camera.source || {};
          const sourceType =
            (source.type as string) ||
            ((camera.camera_id === 'hardware_cam' || rtspUrl.startsWith('rtsp://')) ? (camera.camera_id === 'hardware_cam' ? 'hardware' : 'rtsp') : 'unknown');
          const enabled = camera.enabled !== false;
          const cameraZones = zonesByCamera[camId] || [];
          const cameraPolicies = policiesByCamera[camId] || [];
          const deployedPolicies = cameraPolicies.filter(
            (p: any) => String(p?.deployment_state || '').toUpperCase() === 'DEPLOYED'
          );
          const incidentCount = incidentsByCamera[camId] || 0;
          const edgeInfo = edgeStatus[camId] || {};

          // Determine issues
          const issues: string[] = [];
          const hints: string[] = [];
          if (!enabled) issues.push('Camera is disabled');
          if (cameraPolicies.length === 0) issues.push('No policies configured for this camera');
          if (cameraZones.length === 0 && !cameraPolicies.some((p: any) => p.scope?.camera_ids?.length > 0)) {
            issues.push('No zones configured and no direct camera-scoped policies');
          }
          if (edgeInfo.alive === false) issues.push('Camera pipeline not running on edge');
          if (edgeInfo.source_reachable === false) issues.push('Camera source (RTSP/hardware) not reachable');
          if (cameraPolicies.length > 0 && deployedPolicies.length === 0) {
            issues.push('Policies are configured but not applied on edge yet');
          }
          if (
            incidentCount === 0 &&
            deployedPolicies.length > 0 &&
            edgeInfo.alive !== false &&
            edgeInfo.source_reachable !== false
          ) {
            hints.push(
              `No incidents in last ${hours}h despite deployed policies (check detections in Live View).`
            );
          }

          return {
            camera_id: camId,
            // Align with Camera Workspace title ("camera.name") when present.
            display_name: camera.display_name || camera.name || camera.slug || camId,
            source_type: sourceType,
            enabled,
            zone_count: cameraZones.length,
            policy_count: cameraPolicies.length,
            deployed_policy_count: deployedPolicies.length,
            incident_count_24h: incidentCount,
            edge_alive: edgeInfo.alive ?? null,
            edge_source_reachable: edgeInfo.source_reachable ?? null,
            policies: cameraPolicies.map((p: any) => ({
              policy_id: p.policy_id,
              policy_type: p.policy_type,
              name: p.name || p.policy_id,
              deployment_state: p.deployment_state,
            })),
            issues,
            hints,
          };
        }).filter(Boolean) as CameraDiagnostic[],
      };

      if (reqId !== diagnosticsReqRef.current) return;
      setReport(diagnosticReport);

      if (activeDeviceId) {
        const id = activeDeviceId;
        fetchEdgeStageHealth(id);
        fetchEdgeHealth(id);
        fetchDeviceHeartbeat(id);
        fetchEdgeFps(id);
        scheduleDeferredWork(() => {
          if (reqId !== diagnosticsReqRef.current) return;
          if (deviceIdRef.current !== id) return;
          fetchLastIncident(id);
          fetchDrbStatus(id);
          fetchConfigDrift(id);
          fetchDeviceDiagnosis(id);
          fetchBringupLatest(id);
          fetchRecentActions(id);
        });
      }
    } catch (err: any) {
      if (reqId === diagnosticsReqRef.current) {
        setError(err.message || 'Failed to fetch diagnostic data');
      }
    } finally {
      if (reqId === diagnosticsReqRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchDeviceOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, orgId, siteId]);

  useEffect(() => {
    if (!user) return;
    fetchDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, user, location.search]);

  useEffect(() => {
    if (!user) return;
    fetchIncidentEpisodeRollout(hours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, user, orgId, siteId]);

  useEffect(() => {
    if (!deviceId || !gate1MonitorExpanded) return;
    if (gate1Stages !== null || gate1Alarms !== null || gate1StagesLoading || gate1AlarmsLoading) return;
    fetchGate1Stages(deviceId);
    fetchGate1Alarms(deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, gate1MonitorExpanded, gate1Stages, gate1Alarms, gate1StagesLoading, gate1AlarmsLoading]);

  // Epic 12 Phase A: poll ops state for banner
  useEffect(() => {
    const t = window.setInterval(() => setOpsStateResult(deriveOpsState({})), 3000);
    return () => window.clearInterval(t);
  }, []);

  // Device-scoped panels refresh from fetchDiagnostics (after the main report is built) so we
  // avoid duplicate parallel fanout here and keep one orchestrated path for initial + manual refresh.

  const edgeCloudStatus = () => {
    const codes: string[] = Array.isArray(edgeHealth?.reason_codes) ? edgeHealth.reason_codes : [];
    const detail: any = edgeHealth?.reason_detail || {};
    const unreachable = detail?.cloud_unreachable;
    const authFailed = detail?.cloud_auth_failed;
    if (codes.includes('cloud_unreachable') || unreachable) {
      return { status: 'down', kind: 'cloud_unreachable', detail: unreachable || null };
    }
    if (codes.includes('cloud_auth_failed') || authFailed) {
      return { status: 'down', kind: 'cloud_auth_failed', detail: authFailed || null };
    }
    if (!edgeHealth) {
      return { status: 'unknown', kind: 'unknown', detail: null };
    }
    // Edge is reachable and it did not report a cloud reachability/auth reason code.
    return { status: 'ok', kind: 'ok', detail: null };
  };

  const toggleCameraExpanded = (cameraId: string) => {
    const newExpanded = new Set(expandedCameras);
    if (newExpanded.has(cameraId)) {
      newExpanded.delete(cameraId);
    } else {
      newExpanded.add(cameraId);
    }
    setExpandedCameras(newExpanded);
  };

  const getStatusIcon = (camera: CameraDiagnostic) => {
    if (camera.issues.length === 0 && camera.hints.length === 0) {
      return <CheckIcon color="success" />;
    }
    if (camera.issues.some((i) => i.includes('not running') || i.includes('not reachable'))) {
      return <ErrorIcon color="error" />;
    }
    return <WarningIcon color="warning" />;
  };

  const getStatusColor = (camera: CameraDiagnostic): 'success' | 'warning' | 'error' => {
    if (camera.issues.length === 0 && camera.hints.length === 0) return 'success';
    if (camera.issues.some((i) => i.includes('not running') || i.includes('not reachable'))) return 'error';
    return 'warning';
  };

  const stageChipColor = (status: string | null | undefined) => {
    const s = String(status || '').toLowerCase();
    if (s === 'ok') return 'success';
    if (s === 'degraded') return 'warning';
    if (s === 'stalled') return 'warning';
    if (s === 'down') return 'error';
    return 'default';
  };

  const stageChipIcon = (status: string | null | undefined) => {
    const s = String(status || '').toLowerCase();
    if (s === 'ok') return <CheckIcon fontSize="small" />;
    if (s === 'degraded') return <WarningIcon fontSize="small" />;
    if (s === 'stalled') return <TimerIcon fontSize="small" />;
    if (s === 'down') return <ErrorIcon fontSize="small" />;
    return <WarningIcon fontSize="small" />;
  };

  const canonicalChipColor = (state: string | null | undefined) => {
    const s = String(state || '').toUpperCase();
    if (s === 'OK') return 'success';
    if (s === 'DEGRADED') return 'warning';
    if (s === 'STALLED') return 'warning';
    if (s === 'OFFLINE') return 'error';
    return 'default';
  };

  const canonicalChipIcon = (state: string | null | undefined) => {
    const s = String(state || '').toUpperCase();
    if (s === 'OK') return <CheckIcon fontSize="small" />;
    if (s === 'DEGRADED') return <WarningIcon fontSize="small" />;
    if (s === 'STALLED') return <TimerIcon fontSize="small" />;
    if (s === 'OFFLINE') return <ErrorIcon fontSize="small" />;
    return <WarningIcon fontSize="small" />;
  };

  const legacyStatusFromLocked = (status: string | null | undefined) => {
    const s = String(status || '').toLowerCase();
    if (s === 'healthy') return 'ok';
    if (s === 'critical') return 'down';
    if (s === 'warn' || s === 'warning' || s === 'degraded') return 'degraded';
    return 'unknown';
  };

  const edgeStageRows = useCallback((): any[] => {
    const locked = Array.isArray(edgeStages?.stages_locked) ? edgeStages.stages_locked : [];
    if (locked.length > 0) {
      return locked
        .map((st: any) => {
          const name = String(st?.name || st?.stage || '').trim();
          if (!name) return null;
          const observations = st?.observations && typeof st.observations === 'object' ? st.observations : {};
          const policy = st?.policy && typeof st.policy === 'object' ? st.policy : {};
          const actions = Array.isArray(policy?.actions_active) ? policy.actions_active : [];
          return {
            stage: name,
            status: legacyStatusFromLocked(st?.status),
            locked_status: String(st?.status || 'warn').toLowerCase(),
            since_ts: st?.since_ts,
            reason: st?.reason,
            signals: observations,
            observations,
            policy,
            recommended_actions: actions,
            primary_action: actions.length > 0 ? actions[0] : null,
          };
        })
        .filter(Boolean) as any[];
    }
    return Array.isArray(edgeStages?.stages) ? edgeStages.stages : [];
  }, [edgeStages]);

  const gate1StageLabel = (stageId: string) => {
    const m: Record<string, string> = {
      'edge.input': 'CAMERA_INPUT',
      'edge.supervisor': 'PIPELINE_RUNTIME',
      'edge.policy': 'RULE_EVAL',
      'edge.evidence_capture': 'EVIDENCE_CAPTURE',
      'edge.incident_upload': 'INCIDENT_EMIT',
      'edge.evidence_upload': 'UPLOADER',
      'edge.edge_storage': 'EDGE_STORAGE',
      'edge.network_quality': 'CLOUD_PATH',
      'cloud.incident_ingest': 'CLOUD_INGEST',
      'cloud.evidence_attach': 'CLOUD_EVIDENCE_ATTACH',
      'cloud.evidence_pending': 'CLOUD_EVIDENCE_PENDING',
      'cloud.db': 'CLOUD_DB',
      // Canonical stages (persisted by cloud diagnosis when edge supports canonical_stages)
      'canon.camera_ingest': 'CAMERA_INGEST',
      'canon.decode': 'DECODE',
      'canon.inference': 'INFERENCE',
      'canon.event_router': 'EVENT_ROUTER',
      'canon.evidence_capture': 'EVIDENCE_CAPTURE',
      'canon.evidence_store': 'EVIDENCE_STORE',
      'canon.upload_sync': 'UPLOAD_SYNC',
      'canon.ticketing_sink': 'TICKETING_SINK',
    };
    return m[String(stageId || '')] || stageId;
  };

  /** Short operator hints for Gate 1 stage IDs (IT-friendly, not raw JSON). */
  const gate1ItHint = (stageId: string): string => {
    const s = String(stageId || '').toLowerCase();
    if (s.includes('network_quality')) {
      return 'Usually the cloud/MQTT path or upload queues — see the detail lines under this stage (bucket + reason).';
    }
    if (s.includes('camera') || s.includes('ingest') || s === 'edge.input') {
      return 'Check camera reachability, RTSP/ONVIF URL and credentials, and that the edge can reach the camera subnet.';
    }
    if (s.includes('decode')) {
      return 'Check that video frames are arriving and the decoder is not stuck on codec or resolution issues.';
    }
    if (s.includes('inference')) {
      return 'Check model bundle deploy, device CPU/GPU headroom, and disk for model artifacts.';
    }
    if (s.includes('upload') || s.includes('upload_sync') || s.includes('incident_upload') || s.includes('evidence_upload')) {
      return 'Check cloud credentials, payload size limits, and outbound connectivity for uploads.';
    }
    if (s.includes('storage') || s.includes('evidence_store') || s.includes('edge_storage')) {
      return 'Check free disk space, mount health, and permissions on the edge evidence volume.';
    }
    if (s.includes('policy') || s.includes('rule_eval')) {
      return 'Check policy pack load, evaluation errors in Edge Logs, and device clock skew.';
    }
    if (s.includes('ticketing')) {
      return 'Check ticketing integration credentials and that the sink API is reachable.';
    }
    if (s.includes('cloud.db')) {
      return 'Often a cloud-side database issue — retry when cloud health is green or escalate to operations.';
    }
    if (s.includes('supervisor') || s.includes('pipeline')) {
      return 'Check edge service restarts, OOM kills, and whether a recent deploy left the pipeline in a bad state.';
    }
    return 'Open Edge Logs on this page for recent errors, then retry after fixing connectivity or config.';
  };

  const gate1Triage = useMemo(() => {
    const rows = gate1Stages;
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const normStatus = (st: any) => String(st?.status || '').toLowerCase();
    const stageKey = (st: any) => String(st?.stage || '').trim();
    const rank = (x: string) => {
      const t = String(x || '').toLowerCase();
      if (t === 'down') return 3;
      if (t === 'degraded') return 2;
      if (t === 'unknown') return 1;
      return 0;
    };
    let worst = 'ok';
    for (const r of rows) {
      const st = normStatus(r);
      if (rank(st) > rank(worst)) worst = st;
    }
    if (worst === 'ok') return null;

    const down = rows.filter((r) => normStatus(r) === 'down');
    const driversDown = down.filter((r) => stageKey(r).toLowerCase() !== 'edge');
    let listDown = driversDown.length > 0 ? driversDown : down;
    listDown = [...listDown].sort(
      (a, b) => stageTriagePriority(String(a?.stage)) - stageTriagePriority(String(b?.stage))
    );

    const degraded = rows
      .filter((r) => normStatus(r) === 'degraded' && stageKey(r).toLowerCase() !== 'edge')
      .sort((a, b) => {
        const pa = stageTriagePriority(String(a?.stage));
        const pb = stageTriagePriority(String(b?.stage));
        if (pa !== pb) return pa - pb;
        const ea = typeof a?.error_count_5m === 'number' ? a.error_count_5m : 0;
        const eb = typeof b?.error_count_5m === 'number' ? b.error_count_5m : 0;
        return eb - ea;
      })
      .slice(0, 5);

    const triageSoft =
      listDown.length > 0 &&
      listDown.every((r: any) => {
        const sid = String(r?.stage || '').toLowerCase();
        if (!sid.includes('network_quality')) return false;
        return String(r?.signals?.network_quality?.reason_code || '') === 'OFFLINE_MODE_ACTIVE';
      });

    return { worst, listDown, degraded, triageSoft };
  }, [gate1Stages]);

  const worstStatus = (statuses: any[]) => {
    const rank = (s: any) => {
      const t = String(s || '').toLowerCase();
      if (t === 'down') return 3;
      if (t === 'degraded') return 2;
      if (t === 'unknown') return 1;
      return 0;
    };
    let worst = 'ok';
    for (const s of statuses || []) {
      if (rank(s) > rank(worst)) worst = String(s || 'unknown');
    }
    return worst;
  };

  const getStage = useCallback((stageName: string) => {
    const stages = edgeStageRows();
    return (stages as any[]).find((x: any) => String(x?.stage || '') === stageName) || null;
  }, [edgeStageRows]);

  const getDiagnosisStage = (stageName: string) => {
    const stages = ((deviceDiagnosis?.cloud?.stages || []) as any[]) || [];
    return stages.find((x: any) => String(x?.stage || '') === stageName) || null;
  };

  const getSelectedCameraCount = (): number | null => {
    if (report?.summary && typeof report.summary.total_cameras === 'number') {
      return report.summary.total_cameras;
    }
    const continuity = getStage('camera_continuity');
    const viaSignals = continuity?.signals?.camera_continuity?.camera_count;
    if (typeof viaSignals === 'number') return viaSignals;
    const viaObs = continuity?.observations?.camera_continuity?.camera_count;
    if (typeof viaObs === 'number') return viaObs;
    return null;
  };

  const getInputStageSnapshot = () => {
    const stages = edgeStageRows();
    const input = stages.find((s: any) => String(s?.stage || '') === 'input') || null;
    const status = String(input?.status || edgeStages?.overall_status || 'unknown').toLowerCase();
    const topics = Array.isArray(input?.signals?.topics_seen) ? input.signals.topics_seen : [];
    const probe = input?.signals?.mqtt_probe || null;
    const probeCounts = (probe && typeof probe === 'object' && probe.counts && typeof probe.counts === 'object') ? probe.counts : {};
    const hasTracks =
      topics.some((t: any) => String(t || '').includes('warehouse/tracks')) ||
      Object.keys(probeCounts || {}).some((k) => String(k).includes('warehouse/tracks'));
    const label = status === 'ok' ? (hasTracks ? 'running' : 'ok') : status || 'unknown';
    return {
      status,
      label,
      hasTracks,
      isRunning: label === 'running',
    };
  };

  const runRecommendedAction = async (action: string) => {
    const a = String(action || '');
    if (a === 'check_network_link') {
      if (!deviceId) {
        setEdgeActionResult({ ok: false, action: a, error: 'no_device_selected' });
        return;
      }
      setEdgeActionLoading(true);
      setEdgeActionResult(null);
      try {
        const res = await api.post(
          `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/verify_local`,
          {}
        );
        setVerifyResult(res.data);
        setEdgeActionResult({
          ok: res.data?.ok !== false,
          action: 'check_network_link',
          note: 'Same as Verify local — edge checks proxied through the cloud.',
          result: res.data,
        });
      } catch (e: any) {
        setEdgeActionResult({
          ok: false,
          action: 'check_network_link',
          error: e?.response?.data?.error || e?.message || 'verify_local_failed',
        });
      } finally {
        setEdgeActionLoading(false);
      }
      return;
    }
    if (a === 'sync_desired_state') return syncDesiredStateNow();
    if (a === 'restart_uploaders') return restartUploadersNow();
    if (a === 'run_log_maintenance') return runLogMaintenanceNow();
    if (a === 'reset_failed_evidence_upload' || a === 'reset_failed_outbox') return retryFailedEvidenceOutboxNow();
    if (a === 'replay_incident') {
      const iid = String(incidentOutboxIncidentId || evidenceStateIncidentId || '').trim();
      if (!iid) {
        setEdgeActionResult({ ok: false, error: 'missing_incident_id' });
        return;
      }
      return replayIncidentNow(iid);
    }
    // For now, keep other actions informational.
    setEdgeActionResult({ ok: false, error: `unsupported_action:${a}` });
  };

  const replayIncidentNow = async (incidentId: string) => {
    if (!deviceId || !incidentId) return;
    setEdgeActionLoading(true);
    setEdgeActionResult(null);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/replay`, {
        confirm: 'replay_incident',
        incident_id: String(incidentId).trim(),
        kinds: ['incident', 'evidence'],
      });
      setEdgeActionResult({ ok: true, action: 'replay_incident', result: res.data });
      // Refresh the high-signal views.
      fetchEdgeStageHealth(deviceId);
      fetchDeviceDiagnosis(deviceId);
      fetchGate1Stages(deviceId);
      fetchGate1Alarms(deviceId);
      // Best-effort refresh outbox snapshot if currently shown.
      if (String(incidentOutboxIncidentId || '').trim()) {
        fetchIncidentOutbox(deviceId, String(incidentOutboxIncidentId).trim());
      }
    } catch (e: any) {
      setEdgeActionResult({ ok: false, action: 'replay_incident', error: e?.response?.data?.error || e?.message || 'replay_failed' });
    } finally {
      setEdgeActionLoading(false);
    }
  };

  const runVerifyLocal = async () => {
    if (!deviceId) return;
    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${deviceId}/edge/verify_local`, {});
      setVerifyResult(res.data);
    } catch (e: any) {
      setVerifyResult({ ok: false, error: e?.message || 'verify_failed' });
    } finally {
      setVerifyLoading(false);
    }
  };

  const downloadBundleZip = async (dl: string, fallbackFilename: string) => {
    const downloadResp = await api.get(dl, { responseType: 'blob' as any });
    const blob = downloadResp.data as Blob;
    const cd = (downloadResp.headers?.['content-disposition'] || downloadResp.headers?.['Content-Disposition'] || '') as string;
    let filename = fallbackFilename || 'diagnostic_bundle.zip';
    if (typeof cd === 'string' && cd.includes('filename=')) {
      try {
        filename = cd.split('filename=')[1].trim().replace(/^"|"$/g, '');
      } catch {
        // ignore
      }
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const createBundle = async () => {
    if (!deviceId) return;
    setBundleLoading(true);
    setBundleResult(null);
    setBundleJobId(null);
    setBundleStatusUrl(null);
    setBundleDownloadUrl(null);
    setBundleDownloaded(false);
    try {
      // One-click diagnostic bundle (cloud-assembled ZIP with manifest + unpacked edge bundle).
      const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/diagnostic_bundle`, {});
      const jobId = String(res.data?.job_id || '').trim();
      const statusUrl = String(res.data?.status_url || '').trim();
      const downloadUrl = String(res.data?.download_url || '').trim();
      setBundleResult(res.data);
      if (jobId && statusUrl && downloadUrl) {
        setBundleJobId(jobId);
        setBundleStatusUrl(statusUrl);
        setBundleDownloadUrl(downloadUrl);
      } else {
        setBundleLoading(false);
      }
    } catch (e: any) {
      const ce = classifyAxiosError(e);
      setBundleResult({ ok: false, error: ce.message || e?.message || 'bundle_failed' });
      setBundleLoading(false);
    } finally {
      // Actual completion is driven by polling; keep spinner running until done/error.
    }
  };

  // Poll diagnostic bundle job until completion (then download).
  useEffect(() => {
    if (!bundleJobId || !bundleStatusUrl || !deviceId) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(bundleStatusUrl);
        const data = res.data || {};
        setBundleResult(data);
        const st = String(data?.state || '');
        if (st === 'done' || st === 'error') {
          setBundleLoading(false);
          // Download once when ready.
          if (st === 'done' && bundleDownloadUrl && !bundleDownloaded) {
            try {
              await downloadBundleZip(bundleDownloadUrl, `diagnostic_bundle_${deviceId}.zip`);
              setBundleDownloaded(true);
            } catch (e: any) {
              setBundleResult((prev: any) => ({ ...(prev || {}), download_error: e?.message || 'download_failed' }));
            }
          }
          // Stop polling.
          setBundleJobId(null);
          setBundleStatusUrl(null);
        }
      } catch (e: any) {
        const ce = classifyAxiosError(e);
        setBundleResult({ ok: false, error: ce.message || e?.message || 'bundle_status_failed' });
        setBundleLoading(false);
        setBundleJobId(null);
        setBundleStatusUrl(null);
      }
    };

    poll();
    const t = window.setInterval(poll, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [bundleJobId, bundleStatusUrl, bundleDownloadUrl, bundleDownloaded, deviceId]);

  const fetchGuardrailsAudit = async (id: string, limit: number = 200) => {
    if (!id) return;
    setGuardrailsAuditLoading(true);
    setGuardrailsAuditError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/guardrails/audit/tail`, {
        params: { limit: Math.max(1, Math.min(limit || 200, 2000)) },
      });
      setGuardrailsAudit(res.data || null);
    } catch (e: any) {
      setGuardrailsAudit(null);
      setGuardrailsAuditError(e?.response?.data?.error || e?.message || 'guardrails_audit_failed');
    } finally {
      setGuardrailsAuditLoading(false);
    }
  };

  const fetchThrottleOverride = async (id: string) => {
    if (!id) return;
    setThrottleOverrideLoading(true);
    setThrottleOverrideError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/overrides/throttle`);
      setThrottleOverride(res.data || null);
    } catch (e: any) {
      setThrottleOverride(null);
      setThrottleOverrideError(e?.response?.data?.error || e?.message || 'throttle_override_failed');
    } finally {
      setThrottleOverrideLoading(false);
    }
  };

  const fetchCoordinationStatus = async (id: string) => {
    if (!id) return;
    setCoordinationLoading(true);
    setCoordinationError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/coordination/status`);
      setCoordinationStatus(res.data || null);
    } catch (e: any) {
      setCoordinationStatus(null);
      setCoordinationError(e?.response?.data?.error || e?.message || 'coordination_status_failed');
    } finally {
      setCoordinationLoading(false);
    }
  };

  const setThrottleOverrideState = async (state: 'normal' | 'throttled' | 'halt_evidence') => {
    if (!deviceId) return;
    setThrottleOverrideLoading(true);
    setThrottleOverrideError(null);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/overrides/throttle`, {
        confirm: 'set_throttle_override',
        throttle_state: state,
        ttl_sec: Math.max(10, Math.min(throttleOverrideTtlSec || 900, 7 * 24 * 3600)),
        reason: (throttleOverrideReason || '').trim() || undefined,
        created_by: (user as any)?.email || (user as any)?.username || undefined,
      });
      setThrottleOverride(res.data || null);
    } catch (e: any) {
      setThrottleOverride(null);
      setThrottleOverrideError(e?.response?.data?.error || e?.message || 'throttle_override_set_failed');
    } finally {
      setThrottleOverrideLoading(false);
    }
  };

  const clearThrottleOverride = async () => {
    if (!deviceId) return;
    setThrottleOverrideLoading(true);
    setThrottleOverrideError(null);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/overrides/throttle/clear`, {
        confirm: 'clear_throttle_override',
      });
      setThrottleOverride({ ok: true, cleared: true, result: res.data });
      await fetchThrottleOverride(deviceId);
    } catch (e: any) {
      setThrottleOverrideError(e?.response?.data?.error || e?.message || 'throttle_override_clear_failed');
    } finally {
      setThrottleOverrideLoading(false);
    }
  };

  const fetchUploadersPausedOverride = async (id: string) => {
    if (!id) return;
    setUploadersPausedLoading(true);
    setUploadersPausedError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/overrides/uploaders_paused`);
      setUploadersPaused(res.data || null);
    } catch (e: any) {
      setUploadersPaused(null);
      setUploadersPausedError(e?.response?.data?.error || e?.message || 'uploaders_paused_failed');
    } finally {
      setUploadersPausedLoading(false);
    }
  };

  const fetchOverridesList = async (id: string) => {
    if (!id) return;
    setOverridesListLoading(true);
    setOverridesListError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(id)}/edge/overrides`);
      setOverridesList(res.data || null);
    } catch (e: any) {
      setOverridesList(null);
      setOverridesListError(e?.response?.data?.error || e?.message || 'overrides_list_failed');
    } finally {
      setOverridesListLoading(false);
    }
  };

  const setUploadersPausedOverride = async () => {
    if (!deviceId) return;
    setUploadersPausedLoading(true);
    setUploadersPausedError(null);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/overrides/uploaders_paused`, {
        confirm: 'set_uploaders_paused',
        ttl_sec: Math.max(10, Math.min(uploadersPausedTtlSec || 900, 7 * 24 * 3600)),
        reason: (uploadersPausedReason || '').trim() || undefined,
        created_by: (user as any)?.email || (user as any)?.username || undefined,
      });
      setUploadersPaused(res.data || null);
      await fetchUploadersPausedOverride(deviceId);
    } catch (e: any) {
      setUploadersPaused(null);
      setUploadersPausedError(e?.response?.data?.error || e?.message || 'uploaders_paused_set_failed');
    } finally {
      setUploadersPausedLoading(false);
    }
  };

  const clearUploadersPausedOverride = async () => {
    if (!deviceId) return;
    setUploadersPausedLoading(true);
    setUploadersPausedError(null);
    try {
      const res = await api.post(
        `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/overrides/uploaders_paused/clear`,
        { confirm: 'clear_uploaders_paused' }
      );
      setUploadersPaused(res.data || null);
      await fetchUploadersPausedOverride(deviceId);
    } catch (e: any) {
      setUploadersPausedError(e?.response?.data?.error || e?.message || 'uploaders_paused_clear_failed');
    } finally {
      setUploadersPausedLoading(false);
    }
  };

  const openGenerateReports = (
    kind: ReportDialogKind = 'incidents_rollup'
  ) => {
    const fallbackPath = buildArtifactFlowPath({
      kind,
      deviceId,
    });
    if (kind !== 'proof_pack') {
      navigate(fallbackPath);
      return;
    }
    const proofPreview = readAcceptanceProofCache(orgId, siteId, deviceId || undefined);
    void resolveSafetyReportOpenPath({
      fallbackPath,
      persistedReportId: proofPreview?.persisted_report?.report_id,
    }).then((nextPath) => navigate(nextPath));
  };

  const pushZonesToEdgeAllCameras = async () => {
    if (!deviceId) return;
    setPushZonesLoading(true);
    setPushZonesResult(null);
    try {
      // Cloud API: POST /api/v1/zones/push { device_id, camera_id? }
      const res = await api.post(`/api/v1/zones/push`, { device_id: deviceId });
      setPushZonesResult({ ok: true, ...res.data });
    } catch (e: any) {
      setPushZonesResult({ ok: false, error: e?.response?.data?.error || e?.message || 'push_failed' });
    } finally {
      setPushZonesLoading(false);
    }
  };

  const fetchEdgeLogs = async () => {
    if (!deviceId) return;
    setEdgeLogsLoading(true);
    setEdgeLogsError(null);
    try {
      const params: any = {
        source: 'auto',
        program: edgeLogProgram,
        lines: edgeLogLines,
        since_seconds: edgeLogSinceSeconds,
        json_only: edgeLogJsonOnly ? 'true' : 'false',
        redact: 'true',
      };
      if (edgeLogContains.trim()) params.contains = edgeLogContains.trim();

      const res = await api.get(`/api/v1/pipeline/devices/${deviceId}/edge/logs/tail`, { params });
      setEdgeLogs(res.data as EdgeLogsResponse);
    } catch (e: any) {
      setEdgeLogsError(classifyAxiosError(e).message || 'edge_logs_failed');
      setEdgeLogs(null);
    } finally {
      setEdgeLogsLoading(false);
    }
  };

  // Auto-refresh edge logs
  useEffect(() => {
    if (!edgeLogAutoRefresh || !deviceId) return;
    const ms = Math.max(2, edgeLogRefreshSec) * 1000;
    const t = window.setInterval(() => {
      fetchEdgeLogs();
    }, ms);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeLogAutoRefresh, edgeLogRefreshSec, deviceId, edgeLogProgram, edgeLogLines, edgeLogSinceSeconds, edgeLogJsonOnly, edgeLogContains]);

  const edgeLogsText = (() => {
    const entries = edgeLogs?.entries || [];

    const tryParse = (raw: string): any | null => {
      if (!raw) return null;
      const s = raw.trim();
      if (!s.startsWith('{') || !s.endsWith('}')) return null;
      try {
        return JSON.parse(s);
      } catch {
        return null;
      }
    };

    const filtered = entries.filter((e) => {
      const j = e.json || tryParse(e.raw);
      const raw = String(e.raw || '');
      // contains is already server-side, but keep a client-side fallback in raw mode
      if (edgeLogContains.trim() && !raw.toLowerCase().includes(edgeLogContains.trim().toLowerCase()) && (!j || JSON.stringify(j).toLowerCase().includes(edgeLogContains.trim().toLowerCase()) === false)) {
        return false;
      }
      if (!j) {
        // If structured filters are set, we need JSON to match them.
        if (edgeLogLevel.trim() || edgeLogStage.trim() || edgeLogCameraId.trim() || edgeLogPipelineId.trim()) return false;
        return true;
      }
      const lvl = String(j.level || j.lvl || j.severity || '').toLowerCase();
      const stage = String(j.stage || j.stage_name || '').toLowerCase();
      const cam = String(j.camera_id || j.cam_id || '').toLowerCase();
      const pipe = String(j.pipeline_id || j.pipeline || '').toLowerCase();
      if (edgeLogLevel.trim() && lvl !== edgeLogLevel.trim().toLowerCase()) return false;
      if (edgeLogStage.trim() && stage !== edgeLogStage.trim().toLowerCase()) return false;
      if (edgeLogCameraId.trim() && cam !== edgeLogCameraId.trim().toLowerCase()) return false;
      if (edgeLogPipelineId.trim() && pipe !== edgeLogPipelineId.trim().toLowerCase()) return false;
      return true;
    });

    if (edgeLogView === 'raw') {
      return filtered.map((e) => e.raw).join('\n');
    }
    // pretty: JSON objects when available else raw
    return filtered
      .map((e) => {
        if (e.json) return JSON.stringify(e.json, null, 2);
        const parsed = tryParse(e.raw);
        if (parsed) return JSON.stringify(parsed, null, 2);
        return e.raw;
      })
      .join('\n');
  })();

  const copyEdgeLogs = async () => {
    try {
      await navigator.clipboard.writeText(edgeLogsText || '');
    } catch (e) {
      // ignore
    }
  };

  const downloadEdgeLogs = () => {
    const blob = new Blob([edgeLogsText || ''], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `edge-logs-${deviceId || 'device'}-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const openOnboardDialog = () => {
    // Best-effort defaults:
    // - cloud host: browser hostname (often the cloud host) if available
    // - cloud API URL: default empty (backend will derive from cloud_host when needed)
    setOnboardResult(null);
    if (!onboardEdgeHost) {
      try {
        const ip = String((deviceOptions || []).find((d) => String(d?.device_id || '').trim() === String(deviceId || '').trim())?.ip_address || '').trim();
        if (ip) setOnboardEdgeHost(ip);
      } catch {
        // ignore
      }
    }
    if (!onboardCloudHost) {
      try {
        const h = String(window.location.hostname || '').trim();
        if (h && !isIpv4Literal(h)) setOnboardCloudHost(h);
      } catch {
        // ignore
      }
    }
    if (!onboardCloudApiUrl) {
      try {
        const h = String(window.location.hostname || '').trim();
        if (h && !isIpv4Literal(h)) setOnboardCloudApiUrl(`https://${h}:8443`);
      } catch {
        // ignore
      }
    }
    setOnboardOpen(true);
  };

  const runOnboardEdge = async () => {
    if (!deviceId) return;
    setOnboardLoading(true);
    setOnboardResult(null);
    try {
      const payload: any = {
        edge_host: (onboardEdgeHost || '').trim() || undefined,
        cloud_host: (onboardCloudHost || '').trim() || undefined,
        cloud_api_url: (onboardCloudApiUrl || '').trim() || undefined,
        cloud_mqtt_port: (onboardCloudMqttPort || '').trim() || undefined,
        edge_password: (onboardEdgePassword || '').trim() || undefined,
      };
      // Remove undefined keys to keep payload clean
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/onboard`, payload);
      setOnboardResult(res.data || null);
      try {
        const host = String((res.data as any)?.edge_ip || (payload as any)?.edge_host || '').trim();
        if (host) localStorage.setItem(`edge_host_override:${deviceId}`, host);
      } catch {
        // ignore
      }
    } catch (e: any) {
      setOnboardResult({
        ok: false,
        error: e?.response?.data?.error || e?.message || 'onboard_failed',
        detail: e?.response?.data?.detail,
      });
    } finally {
      setOnboardLoading(false);
    }
  };

  const policiesStatus = String((configDrift?.types?.policies as any)?.status || '').toLowerCase();
  const policyPlaneConverged = policiesStatus === 'converged';
  const policyApplyIssue = 'Policies are configured but not applied on edge yet';
  const camerasForDisplay = useMemo(() => {
    const items = report?.cameras || [];
    if (!policyPlaneConverged) return items;
    return items.map((camera) => {
      if (camera.policy_count <= 0 || camera.deployed_policy_count > 0) return camera;
      const issues = camera.issues.filter((issue) => issue !== policyApplyIssue);
      return {
        ...camera,
        deployed_policy_count: camera.policy_count,
        issues,
      };
    });
  }, [policyPlaneConverged, report]);
  const inputStageSnapshot = getInputStageSnapshot();
  const suppressMissingFpsTelemetryWarning =
    edgeFpsError === 'no health telemetry (missing /mqtt/health heartbeat)' && inputStageSnapshot.isRunning;
  const effectiveEdgeFpsError = suppressMissingFpsTelemetryWarning ? null : edgeFpsError;

  const bringupSummary = useMemo(() => {
    const steps = Array.isArray(bringupLatest?.steps) ? bringupLatest.steps : [];
    const edgeContractStep =
      steps.find((step: any) => String(step?.step_id || '').trim() === 'edge_contract_check') || null;
    const failedStep =
      [...steps].reverse().find((step: any) => String(step?.status || '').trim().toLowerCase() === 'failed') || null;
    const runningStep =
      [...steps].reverse().find((step: any) => String(step?.status || '').trim().toLowerCase() === 'running') || null;
    const artifacts =
      edgeContractStep && typeof edgeContractStep?.artifacts === 'object' && edgeContractStep.artifacts
        ? edgeContractStep.artifacts
        : {};
    const status = String(bringupLatest?.status || '').trim().toLowerCase();
    const lifecycleState = String(artifacts?.lifecycle_state || '').trim();
    const primaryAction = String(artifacts?.primary_action || '').trim();
    const failureFamily = String(artifacts?.failure_family || '').trim();
    const errorDetail = String(
      failedStep?.error_detail || edgeContractStep?.error_detail || bringupLatest?.detail || '',
    ).trim();

    const lifecycleLabel =
      lifecycleState === 'fresh_unit'
        ? 'fresh unit'
        : lifecycleState === 'identity_bootstrap_pending'
          ? 'identity bootstrap pending'
          : lifecycleState === 'reprovision_required'
            ? 'reprovision required'
            : lifecycleState === 'runtime_upgrade_required'
              ? 'runtime upgrade required'
              : lifecycleState
                ? lifecycleState.replace(/_/g, ' ')
                : '';

    const failureLabel =
      failureFamily === 'bootstrap_runtime_missing'
        ? 'base runtime missing'
        : failureFamily === 'bootstrap_identity_missing'
          ? 'identity missing'
          : failureFamily === 'stale_assignment'
            ? 'stale assignment'
            : failureFamily === 'runtime_contract'
              ? 'runtime contract mismatch'
              : failureFamily === 'mtls_trust_mismatch'
                ? 'mTLS trust mismatch'
                : failureFamily === 'missing_device_api_key'
                  ? 'missing device credential'
                  : failureFamily
                    ? failureFamily.replace(/_/g, ' ')
                    : '';

    const actionLabel =
      primaryAction === 'bootstrap_identity'
        ? 'bootstrap identity'
        : primaryAction === 'install_runtime'
          ? 'install runtime'
          : primaryAction === 'reprovision_unit'
            ? 'reprovision unit'
            : primaryAction
              ? primaryAction.replace(/_/g, ' ')
              : '';

    let summary = '';
    if (lifecycleState === 'fresh_unit') {
      summary = 'This unit looks factory-fresh or wiped. Start with edge bootstrap instead of deploy/retry loops.';
    } else if (lifecycleState === 'identity_bootstrap_pending') {
      summary = 'The unit is reachable enough to inspect, but identity/bootstrap has not completed yet.';
    } else if (lifecycleState === 'reprovision_required') {
      summary = 'The edge runtime identity does not match the selected cloud device. Treat this as a reprovision flow, not a normal deploy.';
    } else if (lifecycleState === 'runtime_upgrade_required') {
      summary = 'The edge answered health checks, but its runtime contract is too old or incomplete for this workflow.';
    } else if (status === 'failed') {
      summary = 'Latest bring-up run failed. Use the classified failure below before digging through logs.';
    } else if (status === 'running') {
      summary = 'A bring-up run is in progress. Wait for the current step to finish before retrying.';
    }

    return {
      available: Boolean(bringupLatest || lifecycleState || failureFamily),
      status,
      lifecycleState,
      lifecycleLabel,
      primaryAction,
      actionLabel,
      failureFamily,
      failureLabel,
      failedStepId: String(failedStep?.step_id || '').trim(),
      runningStepId: String(runningStep?.step_id || '').trim(),
      errorDetail,
      summary,
    };
  }, [bringupLatest]);

  const safeRecovery = useMemo<SafeRecoveryModel>(() => {
    const hasDevice = Boolean(String(deviceId || '').trim());
    const heartbeatOk = Boolean(deviceHeartbeat?.ok);
    const heartbeatAge = typeof deviceHeartbeat?.age_s === 'number' ? deviceHeartbeat.age_s : null;
    const heartbeatFresh = heartbeatOk && (heartbeatAge == null ? true : heartbeatAge < 120);

    const driftStatus = String(configDrift?.overall_status || '').trim().toLowerCase();
    const driftNeedsAction = driftStatus === 'drifted' || driftStatus === 'pending';

    const evidenceUploadStage = getStage('evidence_upload') || getStage('incident_upload');
    const evidenceStatus = String(evidenceUploadStage?.status || '').trim().toLowerCase();
    const evidenceRecommended = Array.isArray(evidenceUploadStage?.recommended_actions)
      ? (evidenceUploadStage.recommended_actions as string[])
      : [];
    const evidenceNeedsAction = Boolean(
      evidenceStatus &&
        evidenceStatus !== 'ok' &&
        ['down', 'degraded', 'stalled', 'unknown'].includes(evidenceStatus),
    );

    const ladder = [
      {
        id: 'signals',
        title: 'Refresh + classify signals',
        status: heartbeatFresh ? 'done' : hasDevice ? 'next' : 'blocked',
        detail: hasDevice
          ? heartbeatFresh
            ? `Heartbeat is fresh${heartbeatAge != null ? ` (${Math.round(heartbeatAge)}s)` : ''}.`
            : `Heartbeat is missing/stale${heartbeatAge != null ? ` (${Math.round(heartbeatAge)}s)` : ''}.`
          : 'Select a device to validate edge heartbeat, FPS, and cloud ingest.',
        actionLabel: hasDevice ? 'Troubleshoot' : 'Open Incidents',
      },
      {
        id: 'drift',
        title: 'Converge desired state',
        status: !hasDevice ? 'blocked' : driftNeedsAction ? (heartbeatFresh ? 'next' : 'blocked') : driftStatus ? 'done' : 'ready',
        detail: !hasDevice
          ? 'Device-scoped convergence requires a selected device.'
          : driftNeedsAction
            ? `Config drift: ${driftStatus || 'drifted'}.`
            : driftStatus
              ? `Config drift: ${driftStatus}.`
              : 'Config drift is not loaded yet.',
        actionLabel: 'Sync Desired State',
      },
      {
        id: 'uploads',
        title: 'Unblock evidence pipeline',
        status: !hasDevice ? 'blocked' : evidenceNeedsAction ? (heartbeatFresh ? 'next' : 'blocked') : 'ready',
        detail: !hasDevice
          ? 'Device-scoped upload recovery requires a selected device.'
          : evidenceNeedsAction
            ? `Edge stage ${String(evidenceUploadStage?.stage || 'uploads')} is ${evidenceStatus || 'degraded'}.`
            : 'Retry failed uploads only if backlog is growing or evidence stages are degraded.',
        actionLabel: evidenceRecommended.includes('reset_failed_outbox') || evidenceRecommended.includes('reset_failed_evidence_upload')
          ? 'Retry Failed Evidence Uploads'
          : evidenceRecommended.includes('restart_uploaders')
            ? 'Restart Uploaders'
            : 'Retry Failed Evidence Uploads',
      },
      {
        id: 'escalate',
        title: 'Escalate with context',
        status: hasDevice ? 'ready' : 'ready',
        detail: hasDevice
          ? 'Move to Incidents for queue follow-through, and attach diagnostics context for audit continuity.'
          : 'Use Incidents to triage the queue and select an incident to continue the workflow.',
        actionLabel: 'Open Incidents',
      },
    ] as const;

    const primary = (() => {
      if (!hasDevice) {
        return {
          title: 'Open Incidents',
          detail: 'No device selected. Start from the incident queue, then return here with a device context when needed.',
          label: 'Open Incidents',
          kind: 'nav' as const,
        };
      }
      if (
        bringupSummary.lifecycleState === 'fresh_unit' ||
        bringupSummary.lifecycleState === 'identity_bootstrap_pending' ||
        bringupSummary.lifecycleState === 'reprovision_required' ||
        bringupSummary.failureFamily === 'mtls_trust_mismatch' ||
        bringupSummary.failureFamily === 'missing_device_api_key'
      ) {
        return {
          title: bringupSummary.lifecycleState === 'reprovision_required' ? 'Open edge bootstrap' : 'Onboard Edge (mTLS)',
          detail:
            bringupSummary.errorDetail ||
            bringupSummary.summary ||
            'Bootstrap identity first so the unit can trust the cloud front door and obtain edge credentials.',
          label: bringupSummary.lifecycleState === 'reprovision_required' ? 'Open Bootstrap' : 'Onboard Edge (mTLS)',
          kind: 'onboard' as const,
        };
      }
      if (!heartbeatFresh) {
        return {
          title: 'Troubleshoot',
          detail: 'Start with signal integrity: heartbeat, cloud reachability, then drift.',
          label: 'Troubleshoot',
          kind: 'triage' as const,
        };
      }
      if (driftNeedsAction) {
        return {
          title: 'Sync Desired State',
          detail: 'Converge configuration first; it is the lowest-risk fix for many degraded states.',
          label: 'Sync Desired State',
          kind: 'sync' as const,
        };
      }
      if (evidenceNeedsAction) {
        const canReset = evidenceRecommended.includes('reset_failed_outbox') || evidenceRecommended.includes('reset_failed_evidence_upload');
        const canRestart = evidenceRecommended.includes('restart_uploaders');
        return {
          title: canRestart && !canReset ? 'Restart Uploaders' : 'Retry Failed Evidence Uploads',
          detail: 'Evidence backlog often clears with a safe retry before restart/rollback.',
          label: canRestart && !canReset ? 'Restart Uploaders' : 'Retry Failed Evidence Uploads',
          kind: canRestart && !canReset ? ('restart' as const) : ('retry' as const),
        };
      }
      return {
        title: 'Refresh snapshot',
        detail: 'Signals look stable. Refresh to confirm the state hasn’t changed before taking action.',
        label: 'Refresh snapshot',
        kind: 'refresh' as const,
      };
    })();

    return {
      hasDevice,
      heartbeatFresh,
      driftStatus,
      evidenceNeedsAction,
      ladder,
      primary,
    };
  }, [bringupSummary, deviceId, deviceHeartbeat, configDrift, getStage]);

  const diagnosticsDeviceSelectOptions = useMemo(() => {
    const merged = new Map<string, DeviceOption>(
      [
        ...(deviceId ? ([[String(deviceId), { device_id: String(deviceId) }]] as [string, DeviceOption][]) : []),
        ...(deviceOptions || []).map((d) => [String(d.device_id), d] as [string, DeviceOption]),
        ...(cameraDeviceCandidates || []).map((id) => [String(id), { device_id: String(id) }] as [string, DeviceOption]),
      ].filter((entry) => Boolean(entry?.[0]))
    );
    return Array.from(merged.values())
      .sort((a, b) => String(a?.name || a?.device_id).localeCompare(String(b?.name || b?.device_id)))
      .map((d) => ({
        value: String(d.device_id),
        label: d.name ? `${d.name} — ${d.device_id}` : String(d.device_id),
        secondary: d.ip_address ? String(d.ip_address) : undefined,
      }));
  }, [cameraDeviceCandidates, deviceId, deviceOptions]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <OpsStateBanner
          state={opsStateResult.state}
          reasons={opsStateResult.reasons}
          primaryCta={{ label: 'Refresh', onClick: () => fetchDiagnostics() }}
          secondaryCta={{
            label: 'Copy context',
            onClick: () => {
              const s = buildContextBundle({
                orgId,
                siteId,
                page: 'diagnostics',
                state: opsStateResult.state,
                reasons: opsStateResult.reasons,
                userRole: user?.role,
                deviceId: deviceId ?? undefined,
                extra: {
                  config_drift: configDrift?.overall_status,
                  edge_heartbeat_ok: deviceHeartbeat?.ok,
                  edge_heartbeat_age_s: deviceHeartbeat?.age_s,
                },
              });
              navigator.clipboard.writeText(s).catch(() => {});
            },
          }}
        />
        <Button variant="outlined" size="small" onClick={() => setTriageWizardOpen(true)}>
          Troubleshoot
        </Button>
      </Box>
      {triageRequestedFromSearch && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {incidentIdFromSearch
            ? `Opened in guided triage for incident ${incidentIdFromSearch}. Review the current device state, then return to the incident workspace if more follow-through is needed.`
            : 'Opened in guided triage from the incident review workspace. Review the current device state, then return to the incident if more follow-through is needed.'}
        </Alert>
      )}
      <TriageWizard
        open={triageWizardOpen}
        onClose={() => setTriageWizardOpen(false)}
        checks={{
          uiCloud: { status: opsStateResult.state },
          edgeHeartbeat: deviceHeartbeat,
          configDrift: configDrift ?? undefined,
          postDeployHealth: undefined,
        }}
      />
      {opsStateResult.state === 'unknown' && (
        <PlaybookCallout
          title="Unknown state first response"
          severity="info"
          compact
          summary="Treat unknown as a missing signal first; verify heartbeat, cloud reachability, and drift before taking action."
          doItems={[
            'Refresh health and confirm whether this is just a fresh load or stale telemetry.',
            'Use Troubleshoot to identify whether the issue is edge unreachable, cloud unreachable, or config drift.',
          ]}
          dontItems={[
            'Do not roll back software just because a measurement is temporarily unavailable.',
            'Do not assume the pipeline is broken until heartbeat, cloud front door, and drift checks agree.',
          ]}
          href={runbookDocUrl('fm-d1-edge-unreachable')}
          linkLabel="Open runbook FM-D1"
          sx={{ mb: 2 }}
        />
      )}
      {canReadDiagnostics && !canOperate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only diagnostics access. Health, drift, and evidence inspection are available; device control and remediation actions are disabled for this account.
        </Alert>
      )}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
            Configure
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ mb: 0.25 }}>
            <BugReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Setup Diagnostics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Troubleshoot device health, configuration convergence, and evidence delivery before you return to incident review or proof.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Open documentation (Diagnostics)">
            <IconButton
              size="small"
              component="a"
              href="/docs/user-guide/diagnostics/#safe-first-action-ladder"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DocsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <WarehouseSelect
            id="diagnostics-device-picker"
            label="Device"
            value={deviceId || ''}
            emptyOption={{ value: '', label: 'All devices' }}
            options={diagnosticsDeviceSelectOptions}
            onChange={(v) => {
              const next = String(v || '').trim() ? String(v).trim() : null;
              setSelectedDeviceId(next, { updateUrl: true });
              fetchDiagnostics(next);
            }}
            disabled={deviceOptionsLoading || loading}
            minWidth={280}
            data-testid="diagnostics-device-select"
          />
          <>
            <Button variant="outlined" size="small" onClick={openPreflight} disabled={loading}>
              Open Preflight
            </Button>
            {deviceId && canOperate && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                startIcon={<UndoIcon />}
                onClick={() => {
                  setEdgeActionResult(null);
                  setRollbackDialogOpen(true);
                }}
                disabled={loading || !canOperate}
              >
                Rollback to known-good
              </Button>
            )}
            {deviceId && (
              <>
                <Button variant="outlined" size="small" onClick={openOnboardDialog} disabled={loading || !canOperate}>
                  Onboard Edge (mTLS)
                </Button>
                <Button variant="outlined" size="small" onClick={runVerifyLocal} disabled={verifyLoading || loading}>
                  {verifyLoading ? 'Verifying…' : 'Run Local Verify'}
                </Button>
                <Button variant="outlined" size="small" onClick={createBundle} disabled={bundleLoading || loading}>
                  {bundleLoading ? 'Bundling…' : 'Diagnostic Bundle'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={pushZonesToEdgeAllCameras}
                  disabled={pushZonesLoading || loading || !canOperate}
                >
                  {pushZonesLoading ? 'Pushing Zones…' : 'Push Zones to Edge (all cameras)'}
                </Button>
              </>
            )}
          </>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setHours(hours === 24 ? 48 : 24)}
          >
            {hours}h incidents
          </Button>
          <Button variant="outlined" size="small" onClick={() => openGenerateReports()} disabled={loading}>
            Open artifact tasks
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openGenerateReports('reliability_bundle')}
            disabled={loading}
          >
            Reliability Bundle
          </Button>
          <Button variant="outlined" size="small" onClick={() => openGenerateReports('proof_pack')} disabled={loading}>
            Incident evidence package
          </Button>
          <IconButton onClick={() => fetchDiagnostics()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {deviceOptionsError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDeviceOptionsError(null)}>
          Device list unavailable: {deviceOptionsError}
        </Alert>
      )}

      {deviceId && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Edge device: <strong>{deviceId}</strong>
          </Typography>
        </Box>
      )}

      {canManageCloudCleanup && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1">Cloud incident data cleanup</Typography>
              <Typography variant="body2" color="text.secondary">
                Preview or delete cloud incident, incident-evidence, and optional pending-evidence rows older than a cutoff.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Scope: {orgId || 'all orgs'} / {siteId || 'all sites'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                type="date"
                label="Delete before"
                value={cloudCleanupBefore}
                onChange={(e) => setCloudCleanupBefore(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cloudCleanupIncludePending}
                    onChange={(e) => setCloudCleanupIncludePending(e.target.checked)}
                  />
                }
                label="Include pending evidence"
              />
              <Button
                variant="outlined"
                size="small"
                onClick={runCloudIncidentCleanupPreview}
                disabled={cloudCleanupLoading || !cloudCleanupBefore}
              >
                {cloudCleanupLoading ? 'Previewing…' : 'Preview cleanup'}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => {
                  setCloudCleanupResult(null);
                  setCloudCleanupDialogOpen(true);
                }}
                disabled={cloudCleanupLoading || !cloudCleanupBefore}
              >
                Delete old cloud data
              </Button>
            </Box>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Non-dry-run delete requires cloud env flag <code>ENABLE_ADMIN_INCIDENT_DATA_CLEANUP=true</code> and explicit confirmation.
          </Alert>
          {cloudCleanupResult && (
            <Alert severity={cloudCleanupResult.ok ? 'success' : 'warning'} sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {cloudCleanupResult.ok
                  ? cloudCleanupResult.dry_run
                    ? 'Cleanup preview ready.'
                    : 'Cleanup completed.'
                  : 'Cleanup request failed.'}
              </Typography>
              {renderCloudCleanupSummary(cloudCleanupResult) && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {renderCloudCleanupSummary(cloudCleanupResult)}
                </Typography>
              )}
              {!cloudCleanupResult.ok && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {String(cloudCleanupResult.error || 'unknown_error')}
                  {cloudCleanupResult.detail ? `: ${String(cloudCleanupResult.detail)}` : ''}
                </Typography>
              )}
            </Alert>
          )}
        </Paper>
      )}

      <DiagnosticsSafeRecoveryPath
        orgId={orgId}
        siteId={siteId}
        deviceId={deviceId}
        canReadAudit={canReadAudit}
        canOperate={canOperate}
        loading={loading}
        safeRecovery={safeRecovery}
        onOpenIncidents={() => navigate(deviceId ? `/incidents?device_id=${encodeURIComponent(deviceId)}` : '/incidents')}
        onOpenAudit={() =>
          navigate(deviceId ? `/audit?resource_type=device&resource_id=${encodeURIComponent(deviceId)}` : '/audit')
        }
        onOpenProofPack={() => openGenerateReports('proof_pack')}
        onOpenTriage={() => setTriageWizardOpen(true)}
        onOpenOnboard={openOnboardDialog}
        onSyncDesiredState={syncDesiredStateNow}
        onRetryFailedEvidenceOutbox={retryFailedEvidenceOutboxNow}
        onRestartUploaders={restartUploadersNow}
        onRefreshSnapshot={() => {
          if (!deviceId) return;
          fetchDeviceHeartbeat(deviceId);
          fetchEdgeFps(deviceId);
          fetchLastIncident(deviceId);
          fetchIncidentEpisodeRollout(hours);
          fetchEdgeStageHealth(deviceId);
          fetchDeviceDiagnosis(deviceId);
          fetchBringupLatest(deviceId);
        }}
        onRollback={() => {
          setEdgeActionResult(null);
          setRollbackDialogOpen(true);
        }}
      />

      {/* Simplified rollback (OTA): Guarded rollback dialog */}
      <GuardedActionDialog
        open={rollbackDialogOpen}
        onClose={() => !rollbackLoading && setRollbackDialogOpen(false)}
        title="Rollback to known-good (OTA)"
        riskTier={2}
        blastRadiusText={`Device: ${deviceId ?? '—'} (1 device)`}
        requiresTypedConfirm={{ phrase: 'ROLLBACK' }}
        requiresReason
        confirmLabel="Request rollback"
        confirmColor="warning"
        loading={rollbackLoading}
        onConfirm={handleRollback}
      >
        <Typography variant="body2" color="text.secondary">
          Requests an OTA rollback to the device’s last known-good release (or previous release if no known-good is recorded). This may interrupt
          pipelines briefly.
        </Typography>
        <PlaybookCallout
          title="Rollback vs retry"
          severity="warning"
          compact
          summary="Rollback is for failed release attempts, not for transient telemetry gaps or draining backlog."
          doItems={[
            'Capture the current attempt state or a screenshot before rolling back.',
            'Try config/evidence recovery first if the device is healthy and only drifted.',
          ]}
          dontItems={[
            'Do not use rollback to fix transient telemetry gaps or slow uploads.',
            'Do not roll back if the device is already on the known-good release.',
          ]}
          href={buildDocsUrl('training/EPIC12_WAREHOUSE_OPERATOR_TRAINING.md', 'rollback-vs-retry')}
          linkLabel="Open operator playbook"
          sx={{ mt: 2 }}
        />
        {edgeActionResult && !edgeActionResult.ok && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {edgeActionResult.error}
          </Alert>
        )}
        {edgeActionResult && edgeActionResult.ok && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Rollback requested. Target release: <strong>{String(edgeActionResult.target_release_id || 'unknown')}</strong>
          </Alert>
        )}
      </GuardedActionDialog>

      <GuardedActionDialog
        open={cloudCleanupDialogOpen}
        onClose={() => !cloudCleanupLoading && setCloudCleanupDialogOpen(false)}
        title="Delete old cloud incident data"
        riskTier={2}
        blastRadiusText={`Cloud incident history before ${cloudCleanupBefore || 'selected cutoff'} for ${orgId || 'all orgs'} / ${siteId || 'all sites'}`}
        requiresTypedConfirm={{ phrase: 'DELETE INCIDENT DATA' }}
        requiresReason
        confirmLabel="Delete old cloud data"
        confirmColor="error"
        loading={cloudCleanupLoading}
        onConfirm={executeCloudIncidentCleanup}
      >
        <Typography variant="body2" color="text.secondary">
          This deletes cloud rows older than the selected cutoff from <code>incidents</code>, <code>incident_evidence</code>, and optionally <code>pending_evidence</code>.
          Use preview first and keep this scoped to the current org/site when possible.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Pending evidence included: <strong>{cloudCleanupIncludePending ? 'yes' : 'no'}</strong>
        </Typography>
        {cloudCleanupResult && !cloudCleanupResult.ok && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {String(cloudCleanupResult.error || 'cleanup_delete_failed')}
            {cloudCleanupResult.detail ? `: ${String(cloudCleanupResult.detail)}` : ''}
          </Alert>
        )}
      </GuardedActionDialog>

      <DiagnosticsOnboardDialog
        open={onboardOpen}
        deviceId={deviceId}
        loading={onboardLoading}
        result={onboardResult}
        edgeHost={onboardEdgeHost}
        cloudHost={onboardCloudHost}
        cloudApiUrl={onboardCloudApiUrl}
        cloudMqttPort={onboardCloudMqttPort}
        edgePassword={onboardEdgePassword}
        onClose={() => setOnboardOpen(false)}
        onRun={runOnboardEdge}
        onEdgeHostChange={setOnboardEdgeHost}
        onCloudHostChange={setOnboardCloudHost}
        onCloudApiUrlChange={setOnboardCloudApiUrl}
        onCloudMqttPortChange={setOnboardCloudMqttPort}
        onEdgePasswordChange={setOnboardEdgePassword}
      />

      {/* Edge Stage Health */}
      {deviceId && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6">Edge Stage Health</Typography>
                <Typography variant="body2" color="text.secondary">
                  Staged diagnosis view (input → policy → evidence). “Unknown” means measurement is unavailable/untrusted.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={syncDesiredStateNow}
                  disabled={edgeActionLoading || edgeStagesLoading}
                >
                  {edgeActionLoading ? 'Working…' : 'Sync Desired State'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={restartUploadersNow}
                  disabled={edgeActionLoading || edgeStagesLoading}
                >
                  {edgeActionLoading ? 'Working…' : 'Restart Uploaders'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={retryFailedEvidenceOutboxNow}
                  disabled={edgeActionLoading || edgeStagesLoading}
                >
                  {edgeActionLoading ? 'Working…' : 'Retry Failed Evidence Uploads'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchEdgeStageHealth(deviceId)}
                  disabled={edgeStagesLoading}
                >
                  {edgeStagesLoading ? 'Refreshing…' : 'Refresh'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchEdgeHealth(deviceId)}
                  disabled={edgeHealthLoading || !deviceId}
                >
                  {edgeHealthLoading ? 'Refreshing health…' : 'Refresh Health'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchDeviceDiagnosis(deviceId)}
                  disabled={!deviceId}
                >
                  Refresh Cloud
                </Button>
              </Box>
            </Box>

            <PlaybookCallout
              title="Guidance: if uploads are stuck"
              severity="info"
              compact
              summary="Only relevant if evidence uploads are failing or backlog is growing. Retry failed uploads after cloud + heartbeat recover. Use rollback only for failed releases."
              doItems={[
                'Retry failed uploads first when the cloud front door and heartbeat are healthy.',
                'Use Sync Desired State for config drift; use rollback only if the latest release attempt is actually failing.',
              ]}
              dontItems={[
                'Do not clear or drop pending evidence unless the operator explicitly accepts data loss.',
                'Do not treat a draining backlog as a reason to restart or roll back software immediately.',
              ]}
              href={runbookDocUrl('fm-e3-evidence-backlog')}
              linkLabel="Open runbook FM-E3"
              sx={{ mt: 2 }}
            />

            {edgeStagesError && (
              <Alert severity={edgeStagesErrorKind === 'network' ? 'info' : 'warning'} sx={{ mt: 2 }}>
                {edgeStagesError}
              </Alert>
            )}

            {deviceDiagnosisError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {deviceDiagnosisError}
              </Alert>
            )}

            {edgeActionResult && (
              <Alert severity={edgeActionResult.ok ? 'success' : 'warning'} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Edge Action</Typography>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(edgeActionResult, null, 2)}</pre>
              </Alert>
            )}

            {/* Operator snapshot (answers: is edge alive, are pipelines running, is cloud ingesting incidents?) */}
            <Box sx={{ mt: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="subtitle1">Operator snapshot</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fast triage: edge liveness (heartbeat), pipeline status/FPS, and last cloud-ingested incident.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (!deviceId) return;
                        fetchDeviceHeartbeat(deviceId);
                        fetchEdgeFps(deviceId);
                        fetchLastIncident(deviceId);
                        fetchIncidentEpisodeRollout(hours);
                      }}
                      disabled={!deviceId || deviceHeartbeatLoading || edgeFpsLoading || lastIncidentLoading || episodeRolloutLoading}
                    >
                      {(deviceHeartbeatLoading || edgeFpsLoading || lastIncidentLoading || episodeRolloutLoading) ? 'Refreshing…' : 'Refresh snapshot'}
                    </Button>
                  </Box>

                  {(deviceHeartbeatError || effectiveEdgeFpsError || lastIncidentError) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {deviceHeartbeatError ? `Heartbeat: ${deviceHeartbeatError}` : null}
                      {(deviceHeartbeatError && (effectiveEdgeFpsError || lastIncidentError)) ? ' · ' : null}
                      {effectiveEdgeFpsError ? `FPS: ${effectiveEdgeFpsError}` : null}
                      {(effectiveEdgeFpsError && lastIncidentError) ? ' · ' : null}
                      {lastIncidentError ? `Last incident: ${lastIncidentError}` : null}
                    </Alert>
                  )}

                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(() => {
                      const ok = Boolean(deviceHeartbeat?.ok);
                      const age = typeof deviceHeartbeat?.age_s === 'number' ? deviceHeartbeat.age_s : null;
                      const ageMsg = typeof age === 'number' ? `${Math.round(age)}s` : '—';
                      const status = ok ? 'online' : 'offline';
                      const color: any = ok && typeof age === 'number' && age < 120 ? 'success' : ok ? 'warning' : 'error';
                      return (
                        <Chip
                          label={`edge heartbeat: ${status}${age ? ` (${ageMsg})` : ''}`}
                          color={color}
                          variant="outlined"
                        />
                      );
                    })()}

                    {(() => {
                      const camCount = getSelectedCameraCount();
                      if (camCount === 0) {
                        return (
                          <Chip
                            label="pipeline: not configured (0 cameras)"
                            color="default"
                            variant="outlined"
                          />
                        );
                      }
                      const label = inputStageSnapshot.label;
                      const st = inputStageSnapshot.status;
                      const color: any = label === 'running' ? 'success' : st === 'down' || label === 'stopped' ? 'error' : 'warning';
                      return (
                        <Chip
                          label={`pipeline: ${label}`}
                          color={color}
                          variant="outlined"
                        />
                      );
                    })()}

                    {(() => {
                      const camCount = getSelectedCameraCount();
                      if (camCount === 0) {
                        return (
                          <Chip
                            label="fps: —"
                            color="default"
                            variant="outlined"
                          />
                        );
                      }
                      const fpsIn = typeof edgeFps?.in === 'number' ? edgeFps.in : null;
                      const fpsOut = typeof edgeFps?.out === 'number' ? edgeFps.out : null;
                      const show = edgeFpsLoading
                        ? '…'
                        : (typeof fpsIn === 'number' || typeof fpsOut === 'number')
                          ? `${typeof fpsIn === 'number' ? fpsIn.toFixed(1) : '—'} in / ${typeof fpsOut === 'number' ? fpsOut.toFixed(1) : '—'} out`
                          : '—';
                      const color: any =
                        typeof fpsOut === 'number'
                          ? fpsOut >= 10
                            ? 'success'
                            : fpsOut > 0
                              ? 'warning'
                              : 'error'
                          : 'default';
                      const suffix = effectiveEdgeFpsError ? ' (!)' : '';
                      return (
                        <Chip
                          label={`fps: ${show}${suffix}`}
                          color={color}
                          variant="outlined"
                        />
                      );
                    })()}

                    {(() => {
                      const ts = String(lastIncident?.created_at || '').trim();
                      if (!ts) {
                        return <Chip label="last incident: —" variant="outlined" />;
                      }
                      const d = new Date(ts);
                      const ageMs = Date.now() - d.getTime();
                      const ageMin = Number.isFinite(ageMs) ? Math.max(0, Math.round(ageMs / 60000)) : null;
                      const ageMsg = typeof ageMin === 'number' ? `${ageMin}m ago` : '—';
                      const color: any =
                        typeof ageMin === 'number'
                          ? ageMin <= 5
                            ? 'success'
                            : ageMin <= 30
                              ? 'warning'
                              : 'default'
                          : 'default';
                      const incidentLabel = diagnosticsIncidentLabel(lastIncident);
                      const evidenceState = diagnosticsIncidentEvidenceState(lastIncident);
                      const proofScopeId = diagnosticsIncidentProofScopeId(lastIncident);
                      const sourceContext = diagnosticsIncidentSourceContext(lastIncident);
                      const labelSuffix = evidenceState ? ` | evidence ${evidenceState.toLowerCase()}` : '';
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            label={`last incident: ${incidentLabel} | ${d.toLocaleString()} (${ageMsg})${labelSuffix}`}
                            color={color}
                            variant="outlined"
                          />
                          {sourceContext ? (
                            <Chip label={`source ${sourceContext.toLowerCase()}`} variant="outlined" />
                          ) : null}
                          {proofScopeId ? (
                            <Chip label={`scope ${proofScopeId}`} variant="outlined" />
                          ) : null}
                        </Box>
                      );
                    })()}

                    {(() => {
                      const drift = Number(episodeRollout?.projection_drift?.drifted_episodes || 0);
                      if (episodeRolloutLoading) {
                        return <Chip label="episode projection: checking…" variant="outlined" />;
                      }
                      if (episodeRolloutError) {
                        return <Chip label="episode projection: unavailable" color="warning" variant="outlined" />;
                      }
                      return (
                        <Chip
                          label={drift > 0 ? `episode projection drift: ${drift}` : 'episode projection: synced'}
                          color={drift > 0 ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      );
                    })()}
                  </Box>

                  {(episodeRolloutError || episodeConvergeError) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {episodeRolloutError ? `Episode projection: ${episodeRolloutError}` : null}
                      {episodeRolloutError && episodeConvergeError ? ' · ' : null}
                      {episodeConvergeError ? `Converge: ${episodeConvergeError}` : null}
                    </Alert>
                  )}

                  {Number(episodeRollout?.projection_drift?.drifted_episodes || 0) > 0 && (
                    <Alert
                      severity="warning"
                      sx={{ mt: 2 }}
                      action={(
                        <Button
                          color="inherit"
                          size="small"
                          onClick={convergeIncidentEpisodeRollout}
                          disabled={episodeConvergeLoading}
                        >
                          {episodeConvergeLoading ? 'Converging…' : 'Converge now'}
                        </Button>
                      )}
                    >
                      <Typography variant="subtitle2">Incident queue/review projection drift</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Persisted episode summaries have drifted from linked incidents. Queue freshness can look right while review opens an
                        older detail row until convergence runs.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Last seen: {Number(episodeRollout?.projection_drift?.stale_last_seen_episodes || 0)} · Latest incident:
                        {' '}{Number(episodeRollout?.projection_drift?.stale_latest_incident_id_episodes || 0)} · Policy:
                        {' '}{Number(episodeRollout?.projection_drift?.stale_policy_id_episodes || 0)} · Evidence:
                        {' '}{Number(episodeRollout?.projection_drift?.stale_evidence_count_episodes || 0)}
                      </Typography>
                      {episodeRollout?.recommended_next_step && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          Next step: {String(episodeRollout.recommended_next_step)}
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {episodeConvergeResult?.ok && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Episode convergence complete</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Refreshed {Number(episodeConvergeResult?.refreshed_episode_count || 0)} episode summaries for the last
                        {' '}{Number(episodeConvergeResult?.hours || 0)} hours.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Drift before: {Number(episodeConvergeResult?.before?.drifted_episodes || 0)}. Drift after:
                        {' '}{Number(episodeConvergeResult?.after?.drifted_episodes || 0)}.
                      </Typography>
                    </Alert>
                  )}

                  {bringupSummary.available && (
                    <Alert
                      severity={
                        bringupSummary.status === 'failed'
                          ? 'warning'
                          : bringupSummary.failureFamily === 'mtls_trust_mismatch' ||
                              bringupSummary.failureFamily === 'missing_device_api_key'
                            ? 'error'
                            : bringupSummary.status === 'succeeded'
                              ? 'success'
                              : 'info'
                      }
                      sx={{ mt: 2 }}
                    >
                      <Typography variant="subtitle2">Bring-up / reprovision state</Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {bringupSummary.status && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`job: ${bringupSummary.status}`}
                            color={
                              bringupSummary.status === 'succeeded'
                                ? 'success'
                                : bringupSummary.status === 'failed'
                                  ? 'error'
                                  : bringupSummary.status === 'running'
                                    ? 'warning'
                                    : 'default'
                            }
                            icon={
                              bringupSummary.status === 'succeeded'
                                ? <CheckIcon fontSize="small" />
                                : bringupSummary.status === 'failed'
                                  ? <ErrorIcon fontSize="small" />
                                  : bringupSummary.status === 'running'
                                    ? <TimerIcon fontSize="small" />
                                    : undefined
                            }
                          />
                        )}
                        {bringupSummary.lifecycleLabel && (
                          <Chip size="small" variant="outlined" label={`lifecycle: ${bringupSummary.lifecycleLabel}`} />
                        )}
                        {bringupSummary.failureLabel && (
                          <Chip size="small" variant="outlined" label={`failure: ${bringupSummary.failureLabel}`} />
                        )}
                        {bringupSummary.actionLabel && (
                          <Chip size="small" variant="outlined" label={`next: ${bringupSummary.actionLabel}`} />
                        )}
                      </Box>
                      {(bringupSummary.summary || bringupSummary.errorDetail) && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {bringupSummary.summary || bringupSummary.errorDetail}
                        </Typography>
                      )}
                      {bringupSummary.summary && bringupSummary.errorDetail && bringupSummary.errorDetail !== bringupSummary.summary && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Detail: {bringupSummary.errorDetail}
                        </Typography>
                      )}
                      {(bringupSummary.failedStepId || bringupSummary.runningStepId) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {bringupSummary.failedStepId
                            ? `Failed step: ${bringupSummary.failedStepId}`
                            : `Running step: ${bringupSummary.runningStepId}`}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(bringupSummary.primaryAction === 'bootstrap_identity' ||
                          bringupSummary.primaryAction === 'install_runtime' ||
                          bringupSummary.lifecycleState === 'reprovision_required' ||
                          bringupSummary.failureFamily === 'mtls_trust_mismatch' ||
                          bringupSummary.failureFamily === 'missing_device_api_key') && (
                          <Button size="small" variant="outlined" onClick={openOnboardDialog}>
                            Open Bootstrap
                          </Button>
                        )}
                        {deviceId && (
                          <Button size="small" variant="text" onClick={() => fetchBringupLatest(deviceId)}>
                            Refresh bring-up
                          </Button>
                        )}
                      </Box>
                    </Alert>
                  )}

                  {(() => {
                    const ok = Boolean(deviceHeartbeat?.ok);
                    const age = typeof deviceHeartbeat?.age_s === 'number' ? deviceHeartbeat.age_s : null;
                    const hbFresh = ok && (age == null || age < 120);
                    if (!hbFresh || !edgeStages) return null;
                    const rows = Array.isArray(edgeStages.stages) ? edgeStages.stages : [];
                    const nq = rows.find((r: any) => String(r?.stage || '') === 'network_quality');
                    if (!nq) return null;
                    const st = String(nq?.status || '').toLowerCase();
                    if (st === 'ok' || !st) return null;
                    const nqs = nq?.signals?.network_quality;
                    const hint =
                      nqs && typeof nqs === 'object'
                        ? ` Live edge reports bucket=${String(nqs.bucket || '')}, reason_code=${String(nqs.reason_code || '')}.`
                        : '';
                    return (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Why heartbeat looks fine but network_quality does not</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          <strong>Device heartbeat</strong> (chip above) comes from the cloud: a recent successful{' '}
                          <code>POST /api/v1/devices/&lt;id&gt;/heartbeat</code> into Redis.{' '}
                          <strong>network_quality</strong> is computed on the edge in{' '}
                          <code>/health/stages</code> from <code>offline_mode.json</code> (hysteresis from that same keepalive
                          loop) <em>plus</em> upload-stage status and outbox backlogs.{hint} They often move together, but they
                          are not one signal. If heartbeat is fresh while <code>network_quality</code> stays{' '}
                          <strong>{st}</strong>, refresh <strong>Edge Stage Health → Refresh</strong>, then on the unit inspect{' '}
                          <code>/var/edgentik/state/offline_mode.json</code> and confirm every container sees the same{' '}
                          <code>OFFLINE_MODE_STATE_PATH</code> volume (split mounts cause exactly this confusion).
                        </Typography>
                      </Alert>
                    );
                  })()}

                  {(() => {
                    const pipeline = String(edgeHealth?.pipeline_status || '').toLowerCase();
                    if (pipeline === 'stopped') {
                      return (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Pipeline is stopped. If this is right after a reboot, it may be warming up. If it stays stopped for &gt;2–3 minutes,
                          restart the orchestrator (Edge Stage Health → “Sync Desired State” or restart orchestrator on the unit).
                        </Alert>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
              </Card>
            </Box>

            {/* Cloud front door reachability (edge -> cloud) */}
            {(edgeHealthError || edgeHealth) && (
              <Box sx={{ mt: 2 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="subtitle1">Cloud front door (edge → cloud)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          This is the fastest way to detect “cloud UI is up, but edge cannot upload” (e.g. `api_proxy :8443` down).
                        </Typography>
                      </Box>
                      {(() => {
                        const s = edgeCloudStatus();
                        return (
                          <Chip
                            label={`edge→cloud: ${s.status}`}
                            color={stageChipColor(s.status)}
                            icon={stageChipIcon(s.status)}
                            variant="outlined"
                          />
                        );
                      })()}
                    </Box>

                    {edgeHealthError && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        {edgeHealthError}
                      </Alert>
                    )}

                    {!edgeHealthError && edgeHealth && (
                      <Box sx={{ mt: 2 }}>
                        {(() => {
                          const s = edgeCloudStatus();
                          const cloudUrl =
                            (edgeHealth?.cloud?.api_url as string | undefined) ||
                            (edgeHealth?.cloud_api_url as string | undefined) ||
                            (s.detail?.url as string | undefined) ||
                            undefined;

                          if (s.kind === 'cloud_unreachable') {
                            return (
                              <Alert severity="error">
                                <Typography variant="subtitle2">Cloud unreachable from edge</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Target: <code>{cloudUrl || '—'}</code>
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Detail: {String(s.detail?.error || s.detail?.status || 'unreachable')}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  Fix: ensure the cloud mTLS front door is running (the edge typically uses <code>:8443</code>). Run{' '}
                                  <code>cd cloud_config_management && ./cloud.sh up</code>.
                                </Typography>
                              </Alert>
                            );
                          }

                          if (s.kind === 'cloud_auth_failed') {
                            return (
                              <Alert severity="error">
                                <Typography variant="subtitle2">Cloud auth failed (edge upload credentials)</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Target: <code>{cloudUrl || '—'}</code>
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Hint: check <code>DEVICE_API_KEY</code> on edge (or allow control-plane fallback).
                                </Typography>
                              </Alert>
                            );
                          }

                          return (
                            <Alert severity="success">
                              <Typography variant="subtitle2">Edge can reach cloud</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Target: <code>{cloudUrl || '—'}</code>
                              </Typography>
                              {Array.isArray(edgeHealth?.reason_codes) && edgeHealth.reason_codes.length > 0 && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Other reasons: <code>{edgeHealth.reason_codes.join(', ')}</code>
                                </Typography>
                              )}
                            </Alert>
                          );
                        })()}
                      </Box>
                    )}

                    {/* DRB desired vs applied (BlueprintIntegrationV1) */}
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1">Blueprint / DRB (desired vs applied)</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => deviceId && fetchDrbStatus(deviceId)}
                          disabled={!deviceId || drbStatusLoading}
                        >
                          {drbStatusLoading ? 'Refreshing…' : 'Refresh DRB'}
                        </Button>
                      </Box>

                      {drbStatusError && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          {drbStatusError}
                        </Alert>
                      )}

                      {!drbStatusError && drbStatus && (
                        <Box sx={{ mt: 2 }}>
                          {(() => {
                            const desired = drbStatus?.desired || null;
                            const applied = drbStatus?.applied || null;
                            const hasAny =
                              Boolean(desired) ||
                              Boolean(applied) ||
                              Boolean(edgeHealth?.drb?.present);
                            if (!hasAny) {
                              return (
                                <Alert severity="info">
                                  No DRB/blueprint deployed for this device yet.
                                </Alert>
                              );
                            }
                            return (
                              <>
                                <Typography variant="body2">
                                  Desired:{' '}
                                  <code>
                                    {String(drbStatus?.desired?.bundle_hash || drbStatus?.desired?.hash || '—')} v
                                    {String(drbStatus?.desired?.bundle_version || drbStatus?.desired?.version || '—')}
                                  </code>
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Applied:{' '}
                                  <code>
                                    {String(drbStatus?.applied?.applied_bundle_hash || drbStatus?.applied?.hash || '—')} v
                                    {String(drbStatus?.applied?.applied_bundle_version || drbStatus?.applied?.version || '—')}
                                  </code>{' '}
                                  status=<code>{String(drbStatus?.applied?.status || '—')}</code>
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Edge /health.drb:{' '}
                                  <code>{edgeHealth?.drb?.present ? 'present' : 'absent'}</code>{' '}
                                  main={String(edgeHealth?.drb?.main_blueprint?.hash || edgeHealth?.drb?.main_blueprint?.path || '—')}
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      )}
                    </Box>

                    {/* Drift Dashboard: config_drift (cloud vs device; edge-merged when reachable) */}
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="subtitle1">Configuration sync</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            Cloud vs deploy path on the device, plus live rules-engine detail when you expand a row.
                            Use the IT summary and “Copy for ticket” before escalating.
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {hasDriftedPoliciesOrZones && (
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={handleConvergeDrift}
                              disabled={!deviceId || convergeLoading}
                            >
                              {convergeLoading ? 'Converging…' : 'Converge'}
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => deviceId && fetchConfigDrift(deviceId)}
                            disabled={!deviceId || configDriftLoading}
                          >
                            {configDriftLoading ? 'Refreshing…' : 'Refresh'}
                          </Button>
                        </Box>
                      </Box>

                      {convergeError && (
                        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setConvergeError(null)}>
                          {convergeError}
                        </Alert>
                      )}

                      {configDrift?.edge_merge?.succeeded === false && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Edge unreachable — showing DB-only applied state
                        </Alert>
                      )}

                      {configDriftError && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          {configDriftError}
                        </Alert>
                      )}

                      {!configDriftError && configDrift && (
                        <Box sx={{ mt: 2 }}>
                          {(() => {
                            const types = configDrift.types || {};
                            const drbSt = (types.drb as any)?.status;
                            const fcpSt = (types.fcp as any)?.status;
                            const linesSt = (types.lines as any)?.status;
                            const allCoreUnknown =
                              (drbSt === 'unknown' || !drbSt) &&
                              (fcpSt === 'unknown' || !fcpSt) &&
                              (linesSt === 'unknown' || !linesSt);
                            if (allCoreUnknown) {
                              return (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  No DRB/blueprint deployed for this device yet. <strong>Unknown</strong> for drb/fcp/lines means no deployment exists. Policies and zones status is shown below.
                                </Alert>
                              );
                            }
                            return null;
                          })()}
                          {deviceId && <ConfigDriftItSummary data={configDrift} deviceId={deviceId} />}
                          <DriftTable data={configDrift} />
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Recent actions (audit) for guardrail visibility */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">Recent actions (this device)</Typography>
                <Button variant="text" size="small" onClick={() => deviceId && fetchRecentActions(deviceId)} disabled={recentActionsLoading}>
                  {recentActionsLoading ? 'Loading…' : 'Refresh actions'}
                </Button>
              </Box>
              {recentActionsError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {recentActionsError}
                </Alert>
              )}
              {!recentActionsError && recentActions.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No recent actions yet.
                </Typography>
              )}
              {recentActions.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {recentActions.slice(0, 10).map((a: any, i: number) => (
                    <Box key={i} sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="caption">
                        <strong>{a?.timestamp ? new Date(a.timestamp).toLocaleString() : '—'}</strong>
                        {' · '}
                        {String(a?.action || 'ACTION')}
                        {' · '}
                        {String(a?.success ? 'ok' : 'fail')}
                        {a?.error_message ? ` · ${String(a.error_message).slice(0, 120)}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {(edgeStages || deviceDiagnosis) && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                    <Typography variant="subtitle2">Pipeline &amp; stage signals</Typography>
                    {(() => {
                      const ui = uiHealthStatus();
                      return (
                        <Chip
                          size="small"
                          label={`ui: ${ui.status}`}
                          color={stageChipColor(ui.status)}
                          icon={stageChipIcon(ui.status)}
                          variant="outlined"
                          clickable
                          onClick={() => setEdgeStageSelected((prev) => (prev === 'ui' ? null : 'ui'))}
                        />
                      );
                    })()}
                    {edgeStages && (
                      <>
                        <Tooltip
                          title={
                            <Typography variant="caption" component="span" sx={{ display: 'block', maxWidth: 320 }}>
                              Worst status across edge subsystems (input, uploads, network quality, etc.). A non-ok rollup
                              often means the cloud/MQTT path or queues are stressed — local capture can still be running.
                              Use per-stage chips for detail.
                            </Typography>
                          }
                          arrow
                        >
                          <Chip
                            size="small"
                            label={`Health rollup: ${edgeStages.overall_status || 'unknown'}`}
                            color={stageChipColor(edgeStages.overall_status)}
                            icon={stageChipIcon(edgeStages.overall_status)}
                            variant="outlined"
                          />
                        </Tooltip>
                        {edgeStages?.overall_state && (
                          <Chip
                            size="small"
                            label={`canonical: ${String(edgeStages.overall_state)}`}
                            color={canonicalChipColor(edgeStages.overall_state)}
                            icon={canonicalChipIcon(edgeStages.overall_state)}
                            variant="outlined"
                          />
                        )}
                      </>
                    )}
                    {(deviceDiagnosis?.cloud?.stages || []).length > 0 && (
                      <Chip
                        size="small"
                        label={`cloud overall: ${worstStatus((deviceDiagnosis?.cloud?.stages || []).map((x: any) => x?.status))}`}
                        color={stageChipColor(worstStatus((deviceDiagnosis?.cloud?.stages || []).map((x: any) => x?.status)))}
                        icon={stageChipIcon(worstStatus((deviceDiagnosis?.cloud?.stages || []).map((x: any) => x?.status)))}
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPipelineStageChipsExpanded((e) => !e)}
                      endIcon={pipelineStageChipsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {pipelineStageChipsExpanded ? 'Hide all chips' : 'Show all chips'}
                    </Button>
                    <Button variant="text" size="small" onClick={() => navigate('/grafana')}>
                      Open Grafana
                    </Button>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1, maxWidth: 920 }}>
                  <strong>What is <code>network_quality</code>?</strong> It is <strong>not</strong> Wi‑Fi strength or LAN Mbps.
                  It is a <strong>made-up summary row</strong> on the edge that answers: “Is our <strong>cloud + upload</strong>{' '}
                  path in a good state?” It uses a small file the edge writes when{' '}
                  <strong>keepalive HTTP</strong> to the cloud fails/succeeds (offline mode), plus whether{' '}
                  <strong>incident/evidence uploaders and their queues</strong> look healthy. Cameras can still be on; this
                  row can still be red if the edge thinks the <strong>internet path to your cloud</strong> is bad.
                </Typography>
                {!pipelineStageChipsExpanded && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Per-stage edge, canonical, and cloud chips are hidden. Expand to inspect decode, inference, uploads, etc.
                  </Typography>
                )}
                <Collapse in={pipelineStageChipsExpanded}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', pt: 0.5 }}>
                {edgeStages && (
                  <>
                    {edgeStageRows().map((st: any, idx: number) => {
                      const status = String(st?.status || 'unknown');
                      const stageName = String(st?.stage || 'stage');
                      // Add "proof" to chip label: key metric or last success age
                      let proof = '';
                      try {
                        const signals = st?.signals || {};
                        if (stageName === 'input') {
                          const age = signals?.last_seen_age_sec;
                          if (typeof age === 'number') proof = ` (${Math.round(age)}s ago)`;
                        } else if (stageName === 'policy') {
                          const count = signals?.policy_count;
                          if (typeof count === 'number') proof = ` (${count} policies)`;
                        } else if (stageName === 'evidence_capture') {
                          const age = signals?.last_snapshot_age_sec;
                          if (typeof age === 'number') proof = ` (${Math.round(age)}s ago)`;
                        } else if (stageName === 'incident_upload' || stageName === 'evidence_upload') {
                          const pending = signals?.pending_count;
                          const failed = signals?.failed_count;
                          if (typeof pending === 'number' && pending > 0) proof = ` (${pending} pending)`;
                          else if (typeof failed === 'number' && failed > 0) proof = ` (${failed} failed)`;
                          else {
                            const age = signals?.last_sent_age_sec;
                            if (typeof age === 'number') proof = ` (last: ${Math.round(age)}s ago)`;
                          }
                        } else if (stageName === 'network_quality') {
                          const nq = signals?.network_quality;
                          if (nq && typeof nq === 'object') {
                            const b = String(nq.bucket || '').trim();
                            const rc = String(nq.reason_code || '').trim();
                            const pt =
                              typeof nq.pending_total === 'number' && nq.pending_total > 0
                                ? ` · ${nq.pending_total} pending`
                                : '';
                            if (b || rc) proof = ` (${[b, rc].filter(Boolean).join(' · ')}${pt})`;
                          }
                        }
                      } catch (e) {
                        // ignore
                      }
                      const chip = (
                        <Chip
                          label={`${stageName}: ${status}${proof}`}
                          color={stageChipColor(status)}
                          icon={stageChipIcon(status)}
                          variant="outlined"
                          clickable
                          onClick={() => {
                            const nm = String(st?.stage || '');
                            setEdgeStageSelected((prev) => (prev === nm ? null : nm));
                          }}
                        />
                      );
                      if (stageName === 'network_quality') {
                        return (
                          <Tooltip
                            key={`${stageName}-${idx}`}
                            arrow
                            title={
                              <Typography variant="caption" component="span" sx={{ display: 'block', maxWidth: 340 }}>
                                Not physical network stats. This chip = edge software judgment: offline-mode flag (from
                                cloud keepalive) + upload stage health + outbox backlog. Red often means “cloud path /
                                uploads look broken,” not “switch port down.”
                              </Typography>
                            }
                          >
                            <span>{chip}</span>
                          </Tooltip>
                        );
                      }
                      return (
                        <span key={`${stageName}-${idx}`}>
                          {chip}
                        </span>
                      );
                    })}
                    {Array.isArray(edgeStages?.canonical_stages) && edgeStages.canonical_stages.length > 0 && (
                      <>
                        <Divider flexItem orientation="vertical" />
                        {edgeStages.canonical_stages.map((c: any, idx: number) => {
                          const nm = String(c?.stage || 'stage');
                          const state = String(c?.state || 'UNKNOWN');
                          let proof = '';
                          try {
                            const ex = c?.explain || {};
                            const hb = ex?.heartbeat_age_sec;
                            const ok = ex?.last_success_age_sec;
                            if (typeof hb === 'number') proof = ` (hb ${Math.round(hb)}s)`;
                            if (typeof ok === 'number') proof += ` (ok ${Math.round(ok)}s)`;
                          } catch (e) {
                            // ignore
                          }
                          return (
                            <Chip
                              key={`canon-${nm}-${idx}`}
                              label={`${nm}: ${state}${proof}`}
                              color={canonicalChipColor(state)}
                              icon={canonicalChipIcon(state)}
                              variant="outlined"
                            />
                          );
                        })}
                      </>
                    )}
                  </>
                )}
                {(deviceDiagnosis?.cloud?.stages || []).length > 0 && (
                  <>
                    <Divider flexItem orientation="vertical" />
                    {(deviceDiagnosis?.cloud?.stages || []).map((st: any, idx: number) => (
                      <Chip
                        key={`cloud-${String(st?.stage || 'stage')}-${idx}`}
                        label={`${String(st?.stage || 'cloud.stage')}: ${String(st?.status || 'unknown')}`}
                        color={stageChipColor(st?.status)}
                        icon={stageChipIcon(st?.status)}
                        variant="outlined"
                        clickable
                        onClick={() => {
                          const nm = String(st?.stage || '');
                          setEdgeStageSelected((prev) => (prev === nm ? null : nm));
                        }}
                      />
                    ))}
                  </>
                )}
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Gate 1 persisted stage table + active alarms (cloud monitor), collapsible */}
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2">Gate 1 monitor (cloud, persisted)</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
                    {gate1Stages && gate1Stages.length > 0 && (
                      <Tooltip
                        title={
                          <Typography variant="caption" component="span" sx={{ display: 'block', maxWidth: 320 }}>
                            Persisted worst-of snapshot across Gate 1 stages. Same idea as the live health rollup: not
                            the same as “entire site is offline” — open the table or triage text for which subsystem
                            failed.
                          </Typography>
                        }
                        arrow
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${gate1Stages.length} stages · rollup: ${worstStatus(gate1Stages.map((s: any) => s?.status))}`}
                          color={stageChipColor(worstStatus(gate1Stages.map((s: any) => s?.status)))}
                          icon={stageChipIcon(worstStatus(gate1Stages.map((s: any) => s?.status)))}
                        />
                      </Tooltip>
                    )}
                    {gate1Alarms && gate1Alarms.length > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={`${gate1Alarms.length} active alarm(s)`}
                      />
                    )}
                    {gate1Alarms && gate1Alarms.length === 0 && !gate1AlarmsLoading && !gate1AlarmsError && (
                      <Chip size="small" variant="outlined" label="0 active alarms" />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => deviceId && fetchGate1Stages(deviceId)}
                    disabled={gate1StagesLoading || !deviceId}
                  >
                    {gate1StagesLoading ? 'Loading…' : 'Refresh stages'}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => deviceId && fetchGate1Alarms(deviceId)}
                    disabled={gate1AlarmsLoading || !deviceId}
                  >
                    {gate1AlarmsLoading ? 'Loading…' : 'Refresh alarms'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setGate1MonitorExpanded((e) => !e)}
                    endIcon={gate1MonitorExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    {gate1MonitorExpanded ? 'Hide tables' : 'Show tables'}
                  </Button>
                </Box>
              </Box>
                {gate1StagesError && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {gate1StagesError}
                  </Alert>
                )}
                {gate1AlarmsError && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {gate1AlarmsError}
                  </Alert>
                )}
                {gate1Triage && (
                  <Alert
                    severity={
                      gate1Triage.triageSoft ? 'warning' : gate1Triage.listDown.length > 0 ? 'error' : 'warning'
                    }
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      What this means (IT / NOC)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Persisted rollup (worst stage): <strong>{gate1Triage.worst}</strong>. This is{' '}
                      <strong>not</strong> the same as “everything on the edge is dead” — it is the worst subsystem in
                      the snapshot (often cloud/MQTT or upload queues while cameras still run). The <strong>edge</strong>{' '}
                      row in the table, if present, mirrors that rollup.
                    </Typography>
                    {gate1Triage.listDown.length > 0 && (
                      <Box sx={{ mb: gate1Triage.degraded.length > 0 ? 1 : 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Stages reporting <strong>down</strong> (start here; network quality is listed first when
                          present)
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                          {gate1Triage.listDown.map((st: any, i: number) => {
                            const nqLines = describeNetworkQualityFromStage(st);
                            return (
                              <li key={`${String(st?.stage || '')}-${i}`}>
                                <Typography variant="body2" component="span">
                                  <strong>{gate1StageLabel(String(st?.stage || ''))}</strong>
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    ({String(st?.stage || '')})
                                  </Typography>
                                  {' — '}
                                  {gate1ItHint(String(st?.stage || ''))}
                                </Typography>
                                {nqLines.length > 0 && (
                                  <Box
                                    component="ul"
                                    sx={{ m: 0, pl: 2, mt: 0.5, listStyleType: 'disc' }}
                                  >
                                    {nqLines.map((line, j) => (
                                      <li key={j}>
                                        <Typography variant="caption" color="text.secondary">
                                          {line}
                                        </Typography>
                                      </li>
                                    ))}
                                  </Box>
                                )}
                                {String(st?.stage || '')
                                  .toLowerCase()
                                  .includes('network_quality') && (
                                  <Box component="ol" sx={{ m: 0, pl: 2.5, mt: 0.75 }}>
                                    {networkQualityItPlaybook(
                                      String(st?.signals?.network_quality?.reason_code || ''),
                                      String(st?.signals?.network_quality?.bucket || '')
                                    ).map((step, j) => (
                                      <li key={j}>
                                        <Typography variant="caption" color="text.secondary">
                                          {step}
                                        </Typography>
                                      </li>
                                    ))}
                                  </Box>
                                )}
                                {(st?.last_error_code || st?.last_error_summary) && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 0 }}>
                                    Last error: {String(st?.last_error_code || '')}
                                    {st?.last_error_summary
                                      ? ` — ${String(st.last_error_summary).slice(0, 160)}`
                                      : ''}
                                  </Typography>
                                )}
                              </li>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    {gate1Triage.listDown.length === 0 && gate1Triage.degraded.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        No individual stage row is marked down or degraded in the last snapshot — statuses may be
                        unknown or only the rollup row differs. Open the stage table for heartbeat and error columns,
                        or refresh after the edge has run for a few minutes.
                      </Typography>
                    )}
                    {gate1Triage.degraded.length > 0 && (
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Stages still <strong>degraded</strong> (next)
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                          {gate1Triage.degraded.map((st: any, i: number) => {
                            const nqLines = describeNetworkQualityFromStage(st);
                            return (
                              <li key={`deg-${String(st?.stage || '')}-${i}`}>
                                <Typography variant="body2" component="span">
                                  <strong>{gate1StageLabel(String(st?.stage || ''))}</strong>
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    ({String(st?.stage || '')})
                                  </Typography>
                                  {' — '}
                                  {gate1ItHint(String(st?.stage || ''))}
                                </Typography>
                                {nqLines.length > 0 && (
                                  <Box
                                    component="ul"
                                    sx={{ m: 0, pl: 2, mt: 0.5, listStyleType: 'disc' }}
                                  >
                                    {nqLines.map((line, j) => (
                                      <li key={j}>
                                        <Typography variant="caption" color="text.secondary">
                                          {line}
                                        </Typography>
                                      </li>
                                    ))}
                                  </Box>
                                )}
                                {String(st?.stage || '')
                                  .toLowerCase()
                                  .includes('network_quality') && (
                                  <Box component="ol" sx={{ m: 0, pl: 2.5, mt: 0.75 }}>
                                    {networkQualityItPlaybook(
                                      String(st?.signals?.network_quality?.reason_code || ''),
                                      String(st?.signals?.network_quality?.bucket || '')
                                    ).map((step, j) => (
                                      <li key={j}>
                                        <Typography variant="caption" color="text.secondary">
                                          {step}
                                        </Typography>
                                      </li>
                                    ))}
                                  </Box>
                                )}
                              </li>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    {!gate1AlarmsLoading &&
                      !gate1AlarmsError &&
                      (!gate1Alarms || gate1Alarms.length === 0) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                          <strong>Note:</strong> “0 active alarms” only means no open alarm tickets in this monitor.
                          Stage rows can still be down or degraded — use the list above and Edge Logs.
                        </Typography>
                      )}
                  </Alert>
                )}
                {!gate1MonitorExpanded && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Full stage table and alarm list are hidden. Expand to view columns, errors, and acknowledge alarms.
                  </Typography>
                )}
                <Collapse in={gate1MonitorExpanded}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Gate 1 stage table (persisted)
                    </Typography>
                    {gate1Stages === null && !gate1StagesLoading && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Stages not loaded yet — use Refresh stages.
                      </Typography>
                    )}
                    {gate1Stages !== null && gate1Stages.length === 0 && !gate1StagesLoading && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        No persisted stage rows for this device.
                      </Typography>
                    )}
                    {gate1Stages && gate1Stages.length > 0 && (
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          <strong>Last ok</strong> is only set when a persisted snapshot had this stage <strong>ok</strong>.
                          While status is red/yellow the edge sends no success time — the column shows <strong>—</strong>{' '}
                          (we no longer keep an old date, which looked “stuck” for a week).
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Stage</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Last heartbeat</TableCell>
                              <TableCell align="right">HB age</TableCell>
                              <TableCell>
                                <Tooltip title="Only populated when the latest snapshot reported this stage as ok.">
                                  <span>Last ok</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Seconds since last ok; empty when the stage is not ok in the latest snapshot.">
                                  <span>Since last ok (s)</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">5m attempts</TableCell>
                              <TableCell align="right">5m successes</TableCell>
                              <TableCell align="right">5m errors</TableCell>
                              <TableCell>Last error</TableCell>
                              <TableCell>Hints</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {gate1Stages.map((st: any, idx: number) => (
                              <TableRow key={`${String(st?.stage || '')}-${idx}`} hover>
                                <TableCell>
                                  <Typography variant="body2">
                                    <strong>{gate1StageLabel(String(st?.stage || ''))}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {String(st?.stage || '')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={String(st?.status || 'unknown')}
                                    color={stageChipColor(st?.status)}
                                    icon={stageChipIcon(st?.status)}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{st?.last_heartbeat_at ? new Date(st.last_heartbeat_at).toLocaleString() : '—'}</TableCell>
                                <TableCell align="right">
                                  {st?.last_heartbeat_at
                                    ? Math.max(0, Math.round((Date.now() - new Date(st.last_heartbeat_at).getTime()) / 1000))
                                    : '—'}
                                </TableCell>
                                <TableCell>{st?.last_success_at ? new Date(st.last_success_at).toLocaleString() : '—'}</TableCell>
                                <TableCell align="right">
                                  {st?.last_success_at
                                    ? Math.max(0, Math.round((Date.now() - new Date(st.last_success_at).getTime()) / 1000))
                                    : '—'}
                                </TableCell>
                                <TableCell align="right">{typeof st?.attempt_count_5m === 'number' ? st.attempt_count_5m : '—'}</TableCell>
                                <TableCell align="right">{typeof st?.success_count_5m === 'number' ? st.success_count_5m : '—'}</TableCell>
                                <TableCell align="right">{typeof st?.error_count_5m === 'number' ? st.error_count_5m : '—'}</TableCell>
                                <TableCell>
                                  {st?.last_error_code || st?.last_error_summary ? (
                                    <Typography variant="caption">
                                      <strong>{String(st?.last_error_code || '')}</strong>
                                      {st?.last_error_summary ? ` · ${String(st.last_error_summary).slice(0, 120)}` : ''}
                                    </Typography>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    try {
                                      const reasons = st?.signals?.explain?.reasons;
                                      if (Array.isArray(reasons) && reasons.length > 0) {
                                        return (
                                          <Typography variant="caption" color="text.secondary">
                                            {reasons.slice(0, 3).map((r: any) => String(r || '')).filter(Boolean).join(', ')}
                                          </Typography>
                                        );
                                      }
                                      const nqHint = formatGate1NetworkQualityHint(st);
                                      if (nqHint) {
                                        return (
                                          <Typography variant="caption" color="text.secondary">
                                            {nqHint}
                                          </Typography>
                                        );
                                      }
                                    } catch (e) {
                                      // ignore
                                    }
                                    return <Typography variant="caption" color="text.secondary">—</Typography>;
                                  })()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Active alarms
                    </Typography>
                    {gate1Alarms && gate1Alarms.length > 0 && (
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Severity</TableCell>
                              <TableCell>Alarm</TableCell>
                              <TableCell>Stage</TableCell>
                              <TableCell>Last seen</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {gate1Alarms.map((a: any, idx: number) => (
                              <TableRow key={`${String(a?.id || '')}-${idx}`} hover>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={String(a?.severity || 'warning')}
                                    color={String(a?.severity || '').toLowerCase() === 'critical' ? 'error' : 'warning'}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    <strong>{String(a?.alarm_type || 'ALARM')}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {String(a?.message || '').slice(0, 160)}
                                  </Typography>
                                </TableCell>
                                <TableCell>{a?.stage_name ? String(a.stage_name) : '—'}</TableCell>
                                <TableCell>{a?.last_seen_at ? new Date(a.last_seen_at).toLocaleString() : '—'}</TableCell>
                                <TableCell align="right">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => ackGate1Alarm(String(a?.id || ''))}
                                    disabled={Boolean(a?.acknowledged)}
                                  >
                                    {a?.acknowledged ? 'Acked' : 'Acknowledge'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {gate1Alarms === null && !gate1AlarmsLoading && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Alarms not loaded yet — use Refresh alarms.
                      </Typography>
                    )}
                    {gate1Alarms && gate1Alarms.length === 0 && !gate1AlarmsLoading && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        No active alarms.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>

            {/* Stage Drilldown */}
            <Collapse in={Boolean(edgeStageSelected && (edgeStages || deviceDiagnosis))} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Stage details: <strong>{edgeStageSelected}</strong>
                </Typography>

                {(() => {
                  if (edgeStageSelected === 'ui') {
                    const ui = uiHealthStatus();
                    return (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          UI telemetry (client-side)
                        </Typography>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(ui.detail, null, 2)}</pre>
                      </Box>
                    );
                  }
                  const st =
                    (edgeStageSelected ? getStage(edgeStageSelected) : null) ||
                    (edgeStageSelected ? getDiagnosisStage(edgeStageSelected) : null);
                  if (!st) return null;

                  // Show "proof" prominently: last success, reason, key metrics
                  const lastSuccess = st?.last_success_ts;
                  const reason = st?.reason;
                  const status = st?.status;
                  const signals: any = st?.signals || {};

                  // Show proof signals at top (last success, reason, key metrics)
                  return (
                    <Box>
                      {/* Proof signals */}
                      <Box sx={{ mb: 2 }}>
                        {status && status !== 'ok' && reason && (
                          <Alert severity={status === 'down' ? 'error' : 'warning'} sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">Reason: {reason}</Typography>
                          </Alert>
                        )}
                        {lastSuccess && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Last success: <strong>{new Date(lastSuccess).toLocaleString()}</strong>
                            {' '}
                            ({Math.round((Date.now() - new Date(lastSuccess).getTime()) / 1000)}s ago)
                          </Typography>
                        )}
                        {/* Key metrics */}
                        {(() => {
                          const metrics: any[] = [];
                          if (edgeStageSelected === 'input') {
                            const age = signals?.last_seen_age_sec;
                            if (typeof age === 'number') metrics.push({ label: 'Last seen', value: `${Math.round(age)}s ago` });
                          } else if (edgeStageSelected === 'policy') {
                            const count = signals?.policy_count;
                            if (typeof count === 'number') metrics.push({ label: 'Policy count', value: count });
                            const ver = signals?.bundle_version;
                            if (ver) metrics.push({ label: 'Bundle version', value: ver });
                          } else if (edgeStageSelected === 'evidence_capture') {
                            const age = signals?.last_snapshot_age_sec;
                            if (typeof age === 'number') metrics.push({ label: 'Last snapshot', value: `${Math.round(age)}s ago` });
                            const free = signals?.free_space_mb;
                            if (typeof free === 'number') metrics.push({ label: 'Free space', value: `${Math.round(free)} MB` });
                          } else if (edgeStageSelected === 'incident_upload' || edgeStageSelected === 'evidence_upload') {
                            const pending = signals?.pending_count;
                            const sending = signals?.sending_count;
                            const failed = signals?.failed_count;
                            const oldest = signals?.oldest_pending_age_sec;
                            if (typeof pending === 'number') metrics.push({ label: 'Pending', value: pending });
                            if (typeof sending === 'number' && sending > 0) metrics.push({ label: 'Sending', value: sending });
                            if (typeof failed === 'number' && failed > 0) metrics.push({ label: 'Failed', value: failed });
                            if (typeof oldest === 'number' && oldest > 0) metrics.push({ label: 'Oldest pending', value: `${Math.round(oldest)}s` });
                          } else if (edgeStageSelected === 'network_quality') {
                            const nq = signals?.network_quality;
                            if (nq && typeof nq === 'object') {
                              if (nq.bucket != null && String(nq.bucket).trim())
                                metrics.push({ label: 'Bucket', value: String(nq.bucket) });
                              if (nq.reason_code != null && String(nq.reason_code).trim())
                                metrics.push({ label: 'Reason code', value: String(nq.reason_code) });
                              if (typeof nq.pending_total === 'number')
                                metrics.push({ label: 'Pending total', value: nq.pending_total });
                              if (typeof nq.oldest_pending_age_sec === 'number' && nq.oldest_pending_age_sec > 0) {
                                metrics.push({
                                  label: 'Oldest pending',
                                  value: `${Math.round(nq.oldest_pending_age_sec)}s`,
                                });
                              }
                            }
                          }
                          if (metrics.length === 0) return null;
                          return (
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                              {metrics.map((m, i) => (
                                <Chip key={i} label={`${m.label}: ${m.value}`} size="small" variant="outlined" />
                              ))}
                            </Box>
                          );
                        })()}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Render small curated drilldowns for the high-impact stages. */}
                      {edgeStageSelected === 'policy' && (() => {
                        const eff = signals?.effective_policies || {};
                        const edgeTypes = new Set<string>(Object.keys(eff?.policy_types || {}));
                        const expected = expectedPolicyTypes || [];
                        const missing = expected.filter((t) => !edgeTypes.has(String(t)));
                        return (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Deployment correlation (from `effective_policies.json`)
                            </Typography>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(
                                {
                                  deployment_id: eff.deployment_id,
                                  bundle_version: eff.bundle_version,
                                  bundle_hash: eff.bundle_hash,
                                  policy_count: eff.policy_count,
                                  policy_types: eff.policy_types,
                                  age_sec: eff.age_sec,
                                  path: eff.path,
                                },
                                null,
                                2
                              )}
                            </pre>

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                              Expected policy types for this device (derived from enabled cloud policies) vs edge effective bundle
                            </Typography>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(
                                {
                                  expected_types: expected,
                                  edge_types: Array.from(edgeTypes).sort(),
                                  missing_types: missing,
                                  expected_by_camera: expectedPolicyTypesByCamera,
                                },
                                null,
                                2
                              )}
                            </pre>
                          </Box>
                        );
                      })()}

                      {edgeStageSelected === 'evidence_upload' || edgeStageSelected === 'incident_upload' ? (
                        (() => {
                          const k = edgeStageSelected === 'evidence_upload' ? 'outbox_evidence_upload' : 'outbox_incident_create';
                          const kd = edgeStageSelected === 'evidence_upload' ? 'outbox_evidence_diag' : 'outbox_incident_diag';
                          const diag = signals?.[kd] || {};
                          const recentFailed = diag?.recent_failed || [];
                          const recentPending = diag?.recent_pending || [];
                          const suggestedIncidentId =
                            (recentPending?.[0]?.correlation_ids?.incident_id as string) ||
                            (recentFailed?.[0]?.correlation_ids?.incident_id as string) ||
                            '';
                          return (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Outbox stats + recent errors
                              </Typography>
                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(
                                  {
                                    stats: signals?.[k] || null,
                                    diag: diag,
                                  },
                                  null,
                                  2
                                )}
                              </pre>

                              {/* F2.3: Evidence delivery explainability */}
                              {edgeStageSelected === 'evidence_upload' && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Where is my evidence? (per-incident)
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <TextField
                                      size="small"
                                      label="Incident ID"
                                      placeholder={suggestedIncidentId || 'INC-...'}
                                      value={evidenceStateIncidentId}
                                      onChange={(e) => setEvidenceStateIncidentId(e.target.value)}
                                      sx={{ minWidth: 340 }}
                                    />
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() =>
                                        fetchEvidenceState(
                                          deviceId || '',
                                          (evidenceStateIncidentId || suggestedIncidentId || '').trim()
                                        )
                                      }
                                      disabled={evidenceStateLoading || !(deviceId || '').trim()}
                                    >
                                      {evidenceStateLoading ? 'Loading…' : 'Fetch evidence state'}
                                    </Button>
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={() => {
                                        setEvidenceState(null);
                                        setEvidenceStateError(null);
                                        setEvidenceStateIncidentId(suggestedIncidentId || '');
                                      }}
                                      disabled={evidenceStateLoading}
                                    >
                                      Use suggested
                                    </Button>
                                  </Box>
                                  {evidenceStateError && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                      {evidenceStateError}
                                    </Alert>
                                  )}
                                  {evidenceState && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        Derived: {String(evidenceState?.derived?.state || 'unknown')}
                                        {evidenceState?.derived?.reason_code ? ` (${String(evidenceState.derived.reason_code)})` : ''}
                                      </Typography>
                                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(
                                          {
                                            derived: evidenceState?.derived,
                                            cloud: evidenceState?.cloud,
                                            edge_outbox: evidenceState?.edge_outbox,
                                          },
                                          null,
                                          2
                                        )}
                                      </pre>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* Incident upload explainability */}
                              {edgeStageSelected === 'incident_upload' && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Where is my incident? (edge outbox snapshot)
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <TextField
                                      size="small"
                                      label="Incident ID"
                                      placeholder={suggestedIncidentId || 'INC-...'}
                                      value={incidentOutboxIncidentId}
                                      onChange={(e) => setIncidentOutboxIncidentId(e.target.value)}
                                      sx={{ minWidth: 340 }}
                                    />
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const iid = (incidentOutboxIncidentId || suggestedIncidentId || '').trim();
                                        if (!iid) {
                                          setIncidentOutbox(null);
                                          setIncidentOutboxError('incident_id required');
                                          return;
                                        }
                                        fetchIncidentOutbox(deviceId || '', iid);
                                      }}
                                      disabled={incidentOutboxLoading || !(deviceId || '').trim() || !((incidentOutboxIncidentId || suggestedIncidentId || '').trim())}
                                    >
                                      {incidentOutboxLoading ? 'Loading…' : 'Fetch incident outbox'}
                                    </Button>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => replayIncidentNow((incidentOutboxIncidentId || suggestedIncidentId || '').trim())}
                                      disabled={edgeActionLoading || !(deviceId || '').trim() || !((incidentOutboxIncidentId || suggestedIncidentId || '').trim())}
                                    >
                                      Replay incident
                                    </Button>
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={() => {
                                        setIncidentOutbox(null);
                                        setIncidentOutboxError(null);
                                        setIncidentOutboxIncidentId(suggestedIncidentId || '');
                                      }}
                                      disabled={incidentOutboxLoading || !String(suggestedIncidentId || '').trim()}
                                    >
                                      Use suggested
                                    </Button>
                                  </Box>
                                  {incidentOutboxError && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                      {incidentOutboxError}
                                    </Alert>
                                  )}
                                  {incidentOutbox && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        Counts: {JSON.stringify(incidentOutbox?.counts_by_status || {})}
                                      </Typography>
                                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(incidentOutbox, null, 2)}</pre>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* Correlation IDs */}
                              {(recentFailed.length > 0 || recentPending.length > 0) && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Correlation IDs (for traceability)
                                  </Typography>
                                  {(recentFailed.length > 0 || recentPending.length > 0) && (
                                    <Box>
                                      {[...recentFailed, ...recentPending].slice(0, 5).map((job: any, i: number) => (
                                        <Box key={i} sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                                          <Typography variant="caption">
                                            <strong>Job:</strong> {job?.job_id}
                                            {job?.correlation_ids?.incident_id && (
                                              <> | <strong>Incident:</strong> {job.correlation_ids.incident_id}</>
                                            )}
                                            {job?.last_error && (
                                              <> | <strong>Error:</strong> {String(job.last_error).substring(0, 100)}</>
                                            )}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })()
                      ) : null}

                      {edgeStageSelected === 'network_quality' && (() => {
                        const nq = signals?.network_quality;
                        const lines = describeNetworkQualityFromStage(st);
                        return (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Cloud path quality from the edge: offline mode, upload stage health, and outbox backlog
                              (incident + evidence). This drives the <strong>network_quality</strong> stage — not LAN
                              Wi‑Fi bars.
                            </Typography>
                            {lines.length > 0 ? (
                              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                                {lines.map((line, idx) => (
                                  <li key={idx}>
                                    <Typography variant="body2">{line}</Typography>
                                  </li>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                No structured network_quality payload on this stage row.
                              </Typography>
                            )}
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                              What IT can do (in order)
                            </Typography>
                            <Box component="ol" sx={{ pl: 2, mb: 2, m: 0 }}>
                              {networkQualityItPlaybook(
                                String(
                                  (nq && typeof nq === 'object' && nq.reason_code) || ''
                                ),
                                String((nq && typeof nq === 'object' && nq.bucket) || '')
                              ).map((step, idx) => (
                                <li key={idx}>
                                  <Typography variant="body2">{step}</Typography>
                                </li>
                              ))}
                            </Box>
                            {nq && typeof nq === 'object' ? (
                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(nq, null, 2)}
                              </pre>
                            ) : null}
                          </Box>
                        );
                      })()}

                      {edgeStageSelected === 'supervisor' && (() => {
                        const sup = signals?.supervisor || {};
                        const services = sup?.services || {};
                        return (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Supervisor status (desired vs actual, flapping, exit codes)
                            </Typography>
                            {Object.entries(services).map(([name, info]: [string, any]) => (
                              <Box key={name} sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="subtitle2">
                                  {name}: {info?.state || 'unknown'}
                                  {info?.desired_state && (
                                    <> (desired: {info.desired_state}, matches: {String(info?.state_matches_desired ?? '?')})</>
                                  )}
                                  {info?.flapping && <Chip label="FLAPPING" size="small" color="error" sx={{ ml: 1 }} />}
                                </Typography>
                                {info?.last_exit_code !== null && info?.last_exit_code !== undefined && (
                                  <Typography variant="caption" color="text.secondary">
                                    Last exit code: {info.last_exit_code}
                                  </Typography>
                                )}
                                {info?.last_crash_reason && (
                                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                    Last crash: {String(info.last_crash_reason).substring(0, 200)}
                                  </Typography>
                                )}
                                {info?.restart_count_15m !== undefined && (
                                  <Typography variant="caption" color="text.secondary">
                                    Restarts (15m): {info.restart_count_15m}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', marginTop: 2 }}>
                              {JSON.stringify(sup, null, 2)}
                            </pre>
                          </Box>
                        );
                      })()}

                      {/* Fallback: show raw signals. */}
                      {!['ui', 'policy', 'evidence_upload', 'incident_upload', 'supervisor', 'network_quality'].includes(
                        edgeStageSelected || ''
                      ) && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Raw signals
                          </Typography>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(signals, null, 2)}</pre>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

                {/* Contextual recommended actions (stage-driven, primary action highlighted) */}
                {(() => {
                  const st =
                    (edgeStageSelected ? getStage(edgeStageSelected) : null) ||
                    (edgeStageSelected ? getDiagnosisStage(edgeStageSelected) : null);
                  const rec: string[] = st && Array.isArray(st?.recommended_actions) ? st.recommended_actions : [];
                  const primary = st?.primary_action;
                  if (!rec.length) return null;
                  return (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Recommended actions (stage-driven)
                      </Typography>
                      {rec.includes('check_network_link') && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          “Run Verify local” uses the same API as the Verify local button on this page — edge service
                          checks proxied through the cloud.
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {rec.map((a: string) => (
                          <Button
                            key={a}
                            size="small"
                            variant={a === primary ? 'contained' : 'outlined'}
                            color={a === primary ? 'primary' : 'inherit'}
                            onClick={() => runRecommendedAction(a)}
                            disabled={edgeActionLoading || verifyLoading}
                          >
                            {a === primary
                              ? `⭐ ${recommendedActionButtonLabel(a)} (primary)`
                              : recommendedActionButtonLabel(a)}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Collapse>
          </Box>
        </Paper>
      )}

      {verifyResult && (
        <Alert
          severity={
            verifyResult.ok
              ? (Array.isArray(verifyResult.warnings_failed) && verifyResult.warnings_failed.length > 0 ? 'warning' : 'success')
              : 'error'
          }
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2">Local Verify</Typography>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(verifyResult, null, 2)}</pre>
        </Alert>
      )}

      {bundleResult && (
        <Alert severity={bundleResult.ok ? 'success' : 'error'} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              Diagnostic Bundle{bundleResult?.state ? ` (${String(bundleResult.state)})` : ''}
            </Typography>
            {String(bundleResult?.state || '') === 'running' && <CircularProgress size={14} />}
          </Box>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(bundleResult, null, 2)}</pre>
        </Alert>
      )}

      {pushZonesResult && (
        <Alert severity={pushZonesResult.ok ? 'success' : 'error'} sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Push Zones to Edge</Typography>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(pushZonesResult, null, 2)}</pre>
        </Alert>
      )}

      {deviceId && (
        <DiagnosticsEdgeOperationsPanel
          deviceId={deviceId || ''}
          canOperate={canOperate}
          edgeLogsLoading={edgeLogsLoading}
          edgeLogsError={edgeLogsError}
          edgeLogs={edgeLogs}
          edgeLogsText={edgeLogsText}
          edgeLogProgram={edgeLogProgram}
          edgeLogLines={edgeLogLines}
          edgeLogSinceSeconds={edgeLogSinceSeconds}
          edgeLogJsonOnly={edgeLogJsonOnly}
          edgeLogContains={edgeLogContains}
          edgeLogLevel={edgeLogLevel}
          edgeLogStage={edgeLogStage}
          edgeLogCameraId={edgeLogCameraId}
          edgeLogPipelineId={edgeLogPipelineId}
          edgeLogAutoRefresh={edgeLogAutoRefresh}
          edgeLogRefreshSec={edgeLogRefreshSec}
          edgeLogView={edgeLogView}
          edgeLogsSectionExpanded={edgeLogsSectionExpanded}
          guardrailsAuditLoading={guardrailsAuditLoading}
          guardrailsAuditError={guardrailsAuditError}
          guardrailsAudit={guardrailsAudit}
          throttleOverrideLoading={throttleOverrideLoading}
          throttleOverrideError={throttleOverrideError}
          throttleOverride={throttleOverride}
          throttleOverrideTtlSec={throttleOverrideTtlSec}
          throttleOverrideReason={throttleOverrideReason}
          uploadersPausedLoading={uploadersPausedLoading}
          uploadersPausedError={uploadersPausedError}
          uploadersPaused={uploadersPaused}
          uploadersPausedTtlSec={uploadersPausedTtlSec}
          uploadersPausedReason={uploadersPausedReason}
          overridesListLoading={overridesListLoading}
          overridesListError={overridesListError}
          overridesList={overridesList}
          coordinationLoading={coordinationLoading}
          coordinationError={coordinationError}
          coordinationStatus={coordinationStatus}
          fetchEdgeLogs={fetchEdgeLogs}
          copyEdgeLogs={copyEdgeLogs}
          downloadEdgeLogs={downloadEdgeLogs}
          setEdgeLogProgram={setEdgeLogProgram}
          setEdgeLogLines={setEdgeLogLines}
          setEdgeLogSinceSeconds={setEdgeLogSinceSeconds}
          setEdgeLogJsonOnly={setEdgeLogJsonOnly}
          setEdgeLogContains={setEdgeLogContains}
          setEdgeLogLevel={setEdgeLogLevel}
          setEdgeLogStage={setEdgeLogStage}
          setEdgeLogCameraId={setEdgeLogCameraId}
          setEdgeLogPipelineId={setEdgeLogPipelineId}
          setEdgeLogAutoRefresh={setEdgeLogAutoRefresh}
          setEdgeLogRefreshSec={setEdgeLogRefreshSec}
          setEdgeLogView={setEdgeLogView}
          setEdgeLogsSectionExpanded={setEdgeLogsSectionExpanded}
          fetchGuardrailsAudit={fetchGuardrailsAudit}
          setGuardrailsAudit={setGuardrailsAudit}
          setGuardrailsAuditError={setGuardrailsAuditError}
          fetchThrottleOverride={fetchThrottleOverride}
          setThrottleOverrideState={setThrottleOverrideState}
          clearThrottleOverride={clearThrottleOverride}
          setThrottleOverrideTtlSec={setThrottleOverrideTtlSec}
          setThrottleOverrideReason={setThrottleOverrideReason}
          fetchUploadersPausedOverride={fetchUploadersPausedOverride}
          setUploadersPausedOverride={setUploadersPausedOverride}
          clearUploadersPausedOverride={clearUploadersPausedOverride}
          setUploadersPausedTtlSec={setUploadersPausedTtlSec}
          setUploadersPausedReason={setUploadersPausedReason}
          fetchOverridesList={fetchOverridesList}
          fetchCoordinationStatus={fetchCoordinationStatus}
        />
      )}

      {loading && !report && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width={100} />
                  <Skeleton variant="text" width={60} height={36} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {report && (
        <DiagnosticsReportSection
          report={report}
          hours={hours}
          camerasForDisplay={camerasForDisplay}
          expandedCameras={expandedCameras}
          toggleCameraExpanded={toggleCameraExpanded}
          getStatusIcon={getStatusIcon}
          getStatusColor={getStatusColor}
          navigate={navigate}
          deviceId={deviceId || ''}
          policyPlaneConverged={policyPlaneConverged}
        />
      )}
    </Container>
  );
};

export default Diagnostics;
